import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import {
    RemoveRedEye, Cancel, DeleteForever,
    RecentActors, Message, WarningAmber, Send, Person, Phone,
    Search, Close, WifiOff, Wifi, AttachFile, EmojiEmotions,
    Download, Visibility, Sync, MarkEmailRead,
} from '@mui/icons-material';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, TextField, Button, MenuItem, Modal,
    Tabs, Tab, Typography, Grid, Box, CssBaseline, Divider,
    Snackbar, Alert, Chip,
    CircularProgress, Avatar, Badge, InputAdornment, IconButton, Tooltip,
    Paper, LinearProgress, Backdrop,
} from '@mui/material';
import modalStyle      from '../Components/ModalStyle';
import modalStyleLarge from '../Components/ModalStyleLarge';
import axios           from 'axios';
import { io as socketIOClient } from 'socket.io-client';
import { global_link } from '../Components/Globallink';
import WhatsAppCallActions from '../Components/WhatsAppCallActions';
/* Softphone lives in Sidebar (global). Outbound uses window.__waStartOutboundCall from CallActions. */
import { format, isToday, isYesterday, differenceInDays } from 'date-fns';
import WebSocketContext from '../Context/WebSocket';
import EmojiPicker from 'emoji-picker-react';

// ─── debounce ────────────────────────────────────────────────────────────────
const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
};

const normaliseKENumber = (raw) => {
    const stripped = String(raw || '').replace(/[\s\-().+]/g, '').replace(/\D/g, '');
    let e164 = '';
    if (/^0[17]\d{8}$/.test(stripped))        e164 = `254${stripped.substring(1)}`;
    else if (/^254[17]\d{8}$/.test(stripped)) e164 = stripped;
    else if (/^[17]\d{8}$/.test(stripped))    e164 = `254${stripped}`;
    else                                       e164 = stripped; // non-KE — pass through digits only

    const valid = /^254[17]\d{8}$/.test(e164) || (e164.length >= 7 && e164.length <= 15);
    const display = e164.startsWith('254')
        ? `+254 ${e164.substring(3, 6)} ${e164.substring(6, 9)} ${e164.substring(9)}`
        : (e164 ? `+${e164}` : '');
    return { e164, display, valid };
};

// ─── decode JWT for current user name ────────────────────────────────────────
const getCurrentUser = (authToken) => {
    try {
        const token = localStorage.getItem(authToken);
        if (!token) return null;
        const payload = token.split('.')[1];
        return JSON.parse(atob(payload));
    } catch { return null; }
};

// ─── avatar helpers ───────────────────────────────────────────────────────────
const COLORS = ['#1976d2','#388e3c','#f57c00','#7b1fa2','#c62828','#0097a7','#5d4037'];
const avatarColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return COLORS[Math.abs(h) % COLORS.length];
};
const initials = (name = '') =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// ─── normalize for safe comparison (strip non-digits) ────────────────────────
const digitsOnly = (s) => String(s || '').replace(/\D/g, '');

// ─── Message type display ────────────────────────────────────────────────────
const getMessageTypeLabel = (type) => {
    const types = {
        text: '✉️', image: '📷', video: '📹', audio: '🎧',
        document: '📎', location: '📍', contact: '👤', template: '📋',
        call: '📞', voice_call: '📞', call_permission: '🔑',
        call_permission_request: '🔑',
    };
    return types[type] || '✉️';
};

const isCallTimelineType = (type) =>
    ['call', 'voice_call', 'call_permission', 'call_permission_request'].includes(type);

// ─── Format file size ────────────────────────────────────────────────────────
const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ─── WhatsApp Cloud API media size limits ────────────────────────────────────
const WHATSAPP_MEDIA_LIMITS = {
    image: 5 * 1024 * 1024,
    video: 16 * 1024 * 1024,
    audio: 16 * 1024 * 1024,
    document: 100 * 1024 * 1024,
};

const getWhatsAppMediaCategory = (mimeType = '') => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
};

const validateWhatsAppMediaSize = (file) => {
    const category = getWhatsAppMediaCategory(file.type);
    const limit = WHATSAPP_MEDIA_LIMITS[category];
    if (file.size > limit) {
        return {
            valid: false,
            category,
            limit,
            message: `"${file.name}" is too large for WhatsApp (${formatFileSize(file.size)}). `
                + `${category.charAt(0).toUpperCase() + category.slice(1)}s are limited to ${formatFileSize(limit)}.`,
        };
    }
    return { valid: true, category, limit };
};

// ─── module-level axios instance ────────────────────────────────────────────
const api = axios.create({ baseURL: global_link });

// Derive the webchat socket server origin from the API base URL
// (e.g. "https://farmerschoice.callsuite.net/api/v1" -> "https://farmerschoice.callsuite.net")
const WEBCHAT_WS_ORIGIN = (() => {
    try { return new URL(global_link).origin; } catch { return window.location.origin; }
})();
const WEBCHAT_WS_PATH = '/webchat-socket.io/socket.io';

const Integrations = () => {
    const { socket } = useContext(WebSocketContext);

    // ── tabs ─────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);
    const [activeMini2Tab, setActiveMini2Tab] = useState(0);
    const [activeWebchatMiniTab, setActiveWebchatMiniTab] = useState(0); // 0=Live Chats, 1=Archived, 2=Offline Leads

    // ── WhatsApp Config Check ───────────────────────────────────────────────
    const [whatsAppConfigured, setWhatsAppConfigured] = useState(false);
    const [whatsAppUnreadCount, setWhatsAppUnreadCount] = useState(0);

    // WhatsApp policy notice — hide permanently when user chooses "Don't show again"
    const WA_POLICY_NOTICE_KEY = 'whatsapp_policy_notice_dismissed';
    const [showWhatsAppPolicyNotice, setShowWhatsAppPolicyNotice] = useState(() => {
        try {
            return localStorage.getItem(WA_POLICY_NOTICE_KEY) !== '1';
        } catch {
            return true;
        }
    });
    const dismissWhatsAppPolicyNotice = (permanent = false) => {
        setShowWhatsAppPolicyNotice(false);
        if (permanent) {
            try {
                localStorage.setItem(WA_POLICY_NOTICE_KEY, '1');
            } catch { /* ignore */ }
        }
    };

    // ── SMS ───────────────────────────────────────────────────────────────────
    const [data, setData] = useState([]);
    const [templateData, setTemplateData] = useState([]);
    const [templateListData, setTemplateListData] = useState([]);
    const [contactCampaignData, setContactCampaignData] = useState([]);
    const [campaignContactList, setCampaignContactList] = useState([]);
    const [contactData, setContactData] = useState([]);
    const [campaignData, setCampaignData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalTemplateItems, setTotalTemplateItems] = useState(0);
    const [totalContactCampaignItems, setTotalContactCampaignItems] = useState(0);
    const [totalContactItems, setTotalContactItems] = useState(0);
    const [totalCampaignContactItems, setTotalCampaignContactItems] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedOutboxItem, setSelectedOutboxItem] = useState(null);
    const [tempContentValue, setTempContentValue] = useState('');
    const [contactMethod, setContactMethod] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [campaignName, setCampaignName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [enableDisableButton, setEnableDisableButton] = useState('');
    const [uploadButtonStatus, setUploadButtonStatus] = useState('Submit');
    const [tableCampaignName, setTableCampaignName] = useState('Campaign');
    const [tableCampaignId, setTableCampaignId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTemplateTerm, setSearchTemplateTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchContactTerm, setSearchContactTerm] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');

    // ── SMS modals ────────────────────────────────────────────────────────────
    const [openAddTemplateModal, setAddTemplateModal] = useState(false);
    const [openEditTemplateModal, setEditTemplateModal] = useState(false);
    const [openMessageInfoModal, setMessageInfoModal] = useState(false);
    const [openBulkContactModal, setBulkContactModal] = useState(false);
    const [contactModal, setContactModal] = useState(false);
    const [contactMessageModal, setContactMessageModal] = useState(false);
    const [contactConfirmModal, setContactConfirmModal] = useState(false);

    // ── feedback ──────────────────────────────────────────────────────────────
    const [openWait, setOpenWait] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [openError, setOpenError] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    // ── SMS pagination ────────────────────────────────────────────────────────
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [templatepage, setTemplatePage] = useState(0);
    const [templaterowsPerPage, setTemplateRowsPerPage] = useState(10);
    const [campaignpage, setCampaignPage] = useState(0);
    const [campaignrowsPerPage, setCampaignRowsPerPage] = useState(10);
    const [campaignContactpage, setCampaignContactPage] = useState(0);
    const [campaignContactrowsPerPage, setCampaignContactRowsPerPage] = useState(5);
    const [contactpage, setContactPage] = useState(0);
    const [contactrowsPerPage, setContactRowsPerPage] = useState(10);

    // ── WhatsApp ──────────────────────────────────────────────────────────────
    const [whatsAppContacts, setWhatsAppContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [activeContactNum, setActiveContactNum] = useState('');
    const [whatsAppMessagesData, setWhatsAppMessagesData] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchWhatsAppTerm, setSearchWhatsAppTerm] = useState('');
    const [openAddChatModal, setAddChatModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [whatsAppTemplates, setWhatsAppTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [convStats, setConvStats] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFileForUpload, setSelectedFileForUpload] = useState(null);
    const [callActionLoading, setCallActionLoading] = useState(false);
    const [lastCallPermission, setLastCallPermission] = useState(null);

    const [lightboxMedia, setLightboxMedia] = useState(null); 

    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const oldestLoadedDateRef = useRef(null); 

    const [isReconnecting, setIsReconnecting] = useState(false);
    const [, forceConnTick] = useState(0); 

    const [directNumber, setDirectNumber] = useState('');
    const [directNumberError, setDirectNumberError] = useState('');
    const [directNumberPreview, setDirectNumberPreview] = useState('');

    // ── Webchat (live visitor chat) ─────────────────────────────────────────
    const [webchatSessions, setWebchatSessions] = useState([]);
    const [webchatLoading, setWebchatLoading] = useState(false);
    const [webchatSearchTerm, setWebchatSearchTerm] = useState('');
    const [selectedWebchatSession, setSelectedWebchatSession] = useState(null);
    const [webchatMessages, setWebchatMessages] = useState([]);
    const [webchatMessagesLoading, setWebchatMessagesLoading] = useState(false);
    const [webchatNewMessage, setWebchatNewMessage] = useState('');
    const [webchatSending, setWebchatSending] = useState(false);
    const [webchatUnreadCounts, setWebchatUnreadCounts] = useState({});
    const [webchatStatusText, setWebchatStatusText] = useState('');
    const [webchatSocketConnected, setWebchatSocketConnected] = useState(false);
    const webchatSocketRef = useRef(null);
    const selectedWebchatSessionRef = useRef(null);
    useEffect(() => { selectedWebchatSessionRef.current = selectedWebchatSession; }, [selectedWebchatSession]);
    const webchatMessagesEndRef = useRef(null);
    const [webchatVisitorTyping, setWebchatVisitorTyping] = useState(false);
    const webchatTypingTimeoutRef = useRef(null);

    // ── Archived (ended) chats ────────────────────────────────────────────────
    const [archivedSessions, setArchivedSessions] = useState([]);
    const [totalArchivedSessions, setTotalArchivedSessions] = useState(0);
    const [archivedLoading, setArchivedLoading] = useState(false);
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    const [archivedSearchInput, setArchivedSearchInput] = useState('');
    const [archivedPage, setArchivedPage] = useState(0);
    const [archivedRowsPerPage, setArchivedRowsPerPage] = useState(20);

    // ── Offline leads ─────────────────────────────────────────────────────────
    const [offlineLeads, setOfflineLeads] = useState([]);
    const [totalOfflineLeads, setTotalOfflineLeads] = useState(0);
    const [offlineLeadsSearch, setOfflineLeadsSearch] = useState('');
    const [offlineLeadsPage, setOfflineLeadsPage] = useState(0);
    const [offlineLeadsRowsPerPage, setOfflineLeadsRowsPerPage] = useState(10);
    const [selectedOfflineLead, setSelectedOfflineLead] = useState(null);
    const [offlineLeadModal, setOfflineLeadModal] = useState(false);

    // ── refs ──────────────────────────────────────────────────────────────────
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null); 
    const contactSetRef = useRef(null);
    const messageInputRef = useRef(null);
    const fileInputRef = useRef(null);

    const whatsAppConfiguredRef = useRef(false);
    useEffect(() => { whatsAppConfiguredRef.current = whatsAppConfigured; }, [whatsAppConfigured]);

    const whatsAppUnreadCountRef = useRef(0);
    useEffect(() => { whatsAppUnreadCountRef.current = whatsAppUnreadCount; }, [whatsAppUnreadCount]);

    const authToken = process.env.AUTH_TOKEN;

    const getHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem(authToken)}`,
        'Content-Type': 'application/json',
    }), [authToken]);

    const currentUser = getCurrentUser(authToken);

    // ─── Update Sidebar Notification ────────────────────────────────────────
    const updateSidebarNotification = useCallback((count) => {
        window.dispatchEvent(new CustomEvent('whatsAppNotificationUpdate', {
            detail: { count, configured: whatsAppConfiguredRef.current }
        }));
    }, []);

    // ─── Check WhatsApp Configuration ────────────────────────────────────────
    const checkWhatsAppConfig = useCallback(async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            if (!authyToken) return;
            const decoded = getCurrentUser(authToken);
            if (!decoded) return;
            const orgId = decoded.organization_id;

            const res = await api.get('/whatsapp/getconfigsadmin', {
                headers: getHeaders(),
                params: { limit: 10 }
            });

            const configs = res.data?.data || [];
            const hasConfig = configs.some(c => c.organization_id === orgId);
            setWhatsAppConfigured(hasConfig);

            if (hasConfig) {
                try {
                    const unreadRes = await api.get('/whatsapp/unreadcount', {
                        headers: getHeaders(),
                        params: { organization_id: orgId }
                    });
                    const count = unreadRes.data?.count || 0;
                    setWhatsAppUnreadCount(count);
                    updateSidebarNotification(count);
                } catch (err) {
                    console.error('Error fetching WhatsApp unread count:', err);
                }
            }
        } catch (err) {
            console.error('Error checking WhatsApp config:', err);
            setWhatsAppConfigured(false);
        }
    }, [authToken, getHeaders, updateSidebarNotification]);

    // ─── Emoji Picker ────────────────────────────────────────────────────────
    const onEmojiClick = (emojiData) => {
        setNewMessage(prev => prev + emojiData.emoji);
        setShowEmojiPicker(false);
        messageInputRef.current?.focus();
    };

    // ─── File/Media Upload Handler ──────────────────────────────────────────
    const handleMediaUpload = async (file) => {
        if (!contactSetRef.current) {
            setOpenError(true);
            setNotificationMessage('No contact selected');
            return;
        }

        const sizeCheck = validateWhatsAppMediaSize(file);
        if (!sizeCheck.valid) {
            setOpenError(true);
            setNotificationMessage(sizeCheck.message);
            return;
        }

        setIsSending(true);
        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('contact_number', contactSetRef.current);
        formData.append('caption', newMessage || '');

        try {
            const response = await api.post('/whatsapp/sendmedia', formData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem(authToken)}`,
                },
                withCredentials: true,
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                },
            });

            if (response.data?.success) {
                setNewMessage('');
                setSelectedFileForUpload(null);
                setOpenSuccess(true);
                setNotificationMessage('Media sent successfully!');
            }
        } catch (err) {
            console.error('Error uploading media:', err);
            setOpenError(true);
            setNotificationMessage(err.response?.data?.details || 'Failed to send media');
        } finally {
            setIsSending(false);
            setIsUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const sizeCheck = validateWhatsAppMediaSize(file);
        if (!sizeCheck.valid) {
            setOpenError(true);
            setNotificationMessage(sizeCheck.message);
            event.target.value = '';
            return;
        }
        setSelectedFileForUpload(file);
        setNewMessage(`📎 ${file.name}`);
    };

    const handleSendFile = () => {
        if (selectedFileForUpload) {
            handleMediaUpload(selectedFileForUpload);
        }
    };

    // ══════════════════════════════════════════════════════════════════════════
    // SOCKET EVENTS (WhatsApp/Social — main app socket)
    // ══════════════════════════════════════════════════════════════════════════
    const handleWhatsAppSendEvent = useCallback((msgData) => {
        if (digitsOnly(contactSetRef.current) === digitsOnly(msgData?.contact_number)) {
            setWhatsAppMessagesData(prev => [...prev, {
                text_body: msgData.text_body,
                contact_number: msgData.contact_number,
                origin: 'outbound',
                date: msgData.date || new Date().toISOString(),
                sender_name: msgData.sender_name || currentUser?.user_name || 'Agent',
                type: msgData.type || 'text',
                media_url: msgData.media_url || null,
                file_name: msgData.file_name || null,
                file_size: msgData.file_size || null,
            }]);
        }
    }, []);

    const handleWhatsAppReceiveEvent = useCallback((eventData) => {
        if (eventData.type !== 'new_message') return;
        const msgData = eventData.data;
        const isActiveConversation = digitsOnly(contactSetRef.current) === digitsOnly(msgData?.contact_number);

        if (!isActiveConversation) {
            setUnreadCounts(prev => ({
                ...prev,
                [msgData.contact_number]: (prev[msgData.contact_number] || 0) + 1,
            }));
            setWhatsAppUnreadCount(prev => prev + 1);
            updateSidebarNotification(whatsAppUnreadCountRef.current + 1);
        }

        if (isActiveConversation) {
            setWhatsAppMessagesData(prev => [...prev, {
                text_body: msgData.text_body,
                origin: 'inbound',
                date: msgData.date || new Date().toISOString(),
                contact_number: msgData.contact_number,
                sender_name: msgData.sender_name || 'Unknown',
                type: msgData.type || 'text',
                media_url: msgData.media_url || null,
                file_name: msgData.file_name || null,
                file_size: msgData.file_size || null,
            }]);
        }
    }, [updateSidebarNotification]);

    useEffect(() => {
        if (!socket) return;

        const onCallEvent = (eventData) => {
            if (!eventData) return;
            if (eventData.type === 'call_permission') {
                const d = eventData.data || {};
                setLastCallPermission(d);
                setNotificationMessage(
                    `Call permission ${d.response || 'updated'}${d.contact_number ? ` · ${d.contact_number}` : ''}`
                );
                setOpenSuccess(true);
            } else if (eventData.type === 'call_event') {
                const d = eventData.data || {};
                setNotificationMessage(
                    `WhatsApp call: ${d.event || 'event'}${d.direction ? ` (${d.direction})` : ''}`
                );
                setOpenSuccess(true);
            }
        };

        socket.on('WHATSAPP_SEND_EVENT_REC', handleWhatsAppSendEvent);
        socket.on('WHATSAPP_RECIEVE_EVENT', handleWhatsAppReceiveEvent);
        socket.on('WHATSAPP_CALL_EVENT', onCallEvent);

        const bumpTick = () => forceConnTick((n) => n + 1);
        socket.on('connect', bumpTick);
        socket.on('disconnect', bumpTick);
        socket.on('reconnect', bumpTick);
        socket.on('reconnect_attempt', bumpTick);

        return () => {
            socket.off('WHATSAPP_SEND_EVENT_REC', handleWhatsAppSendEvent);
            socket.off('WHATSAPP_RECIEVE_EVENT', handleWhatsAppReceiveEvent);
            socket.off('WHATSAPP_CALL_EVENT', onCallEvent);
            socket.off('connect', bumpTick);
            socket.off('disconnect', bumpTick);
            socket.off('reconnect', bumpTick);
            socket.off('reconnect_attempt', bumpTick);
        };
    }, [socket, handleWhatsAppSendEvent, handleWhatsAppReceiveEvent]);

    const handleManualReconnect = useCallback(() => {
        if (!socket || isReconnecting) return;
        if (socket.connected) return; 
        setIsReconnecting(true);
        try {
            socket.connect();
        } catch (err) {
            console.error('Manual reconnect failed:', err);
        }
        setTimeout(() => setIsReconnecting(false), 3000);
    }, [socket, isReconnecting]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [whatsAppMessagesData]);

    // ══════════════════════════════════════════════════════════════════════════
    // WEBCHAT SOCKET (dedicated connection to the webchat socket server,
    // separate from the main app socket used for WhatsApp/Social above)
    // ══════════════════════════════════════════════════════════════════════════
    useEffect(() => {
        const token = localStorage.getItem(authToken);
        if (!token) return;

        const wsocket = socketIOClient(WEBCHAT_WS_ORIGIN, {
            path: WEBCHAT_WS_PATH,
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
        });
        webchatSocketRef.current = wsocket;

        wsocket.on('connect', () => setWebchatSocketConnected(true));
        wsocket.on('disconnect', () => setWebchatSocketConnected(false));
        wsocket.on('connect_error', (err) => {
            console.error('[Webchat Socket] connect_error:', err.message);
            setWebchatSocketConnected(false);
        });

        // New visitor message — broadcast org-wide by the webchat socket server
        wsocket.on('new_visitor_message', (payload) => {
            const { session_id, message, sender, timestamp } = payload;
            const active = selectedWebchatSessionRef.current;

            if (active && active.session_id === session_id) {
                setWebchatVisitorTyping(false);
                clearTimeout(webchatTypingTimeoutRef.current);
                setWebchatMessages(prev => [...prev, {
                    origin: 'inbound',
                    message,
                    sender_name: sender,
                    date: timestamp || new Date().toISOString(),
                }]);
            } else {
                setWebchatUnreadCounts(prev => ({
                    ...prev,
                    [session_id]: (prev[session_id] || 0) + 1,
                }));
            }

            // Refresh the session list either way so last_message/order stays current
            fetchWebchatSessionsRef.current?.();
        });

        // Visitor is typing in the active session
        wsocket.on('visitor_typing', (payload) => {
            const active = selectedWebchatSessionRef.current;
            if (!active || active.session_id !== payload?.session_id) return;
            setWebchatVisitorTyping(true);
            clearTimeout(webchatTypingTimeoutRef.current);
            webchatTypingTimeoutRef.current = setTimeout(() => {
                setWebchatVisitorTyping(false);
            }, 2500);
        });

        wsocket.on('agent_joined', ({ session_id, agent_name }) => {
            const active = selectedWebchatSessionRef.current;
            if (active && active.session_id === session_id) {
                setWebchatStatusText(`${agent_name} joined`);
            }
        });

        wsocket.on('agent_disconnected', ({ session_id }) => {
            const active = selectedWebchatSessionRef.current;
            if (active && active.session_id === session_id) {
                setWebchatStatusText('Visitor disconnected');
            }
        });

        wsocket.on('session_ended', ({ session_id }) => {
            const active = selectedWebchatSessionRef.current;
            if (active && active.session_id === session_id) {
                setWebchatStatusText('Chat ended');
            }
            fetchWebchatSessionsRef.current?.();
        });

        return () => {
            wsocket.disconnect();
            webchatSocketRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    useEffect(() => {
        webchatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [webchatMessages]);

    // ══════════════════════════════════════════════════════════════════════════
    // WhatsApp helpers
    // ══════════════════════════════════════════════════════════════════════════
    const fetchWhatsAppContacts = useCallback(async (term = '') => {
        setLoading(true);
        try {
            const res = await api.get('/whatsapp/getwhatsappusers', {
                headers: getHeaders(),
                params: { search: term.trim() || undefined },
            });
            const raw = res.data || [];
            const seen = new Set();
            const deduped = raw.filter((c) => {
                const key = digitsOnly(c.contact_number);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setWhatsAppContacts(deduped);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [getHeaders]);

    const debouncedContactSearch = useCallback(
        debounce((t) => fetchWhatsAppContacts(t), 300), [fetchWhatsAppContacts]
    );

    useEffect(() => {
        fetchWhatsAppContacts('');
        checkWhatsAppConfig();
    }, []);

    const fetchWhatsAppTemplates = useCallback(async () => {
        try {
            setOpenWait(true);
            const res = await api.get('/whatsapp/gettemplates', { headers: getHeaders() });
            const list = Array.isArray(res.data?.data) ? res.data.data : [];
            const approved = list.filter(
                (t) => !t.status || String(t.status).toUpperCase() === 'APPROVED'
            );
            setWhatsAppTemplates(approved);
            setShowTemplates(true);
            if (approved.length === 0) {
                setNotificationMessage('No approved WhatsApp templates found for this WABA.');
                setOpenError(true);
            }
        } catch (err) {
            console.error(err);
            const details = err?.response?.data?.details;
            const msg =
                (typeof details === 'string' ? details : details?.error?.message) ||
                err?.response?.data?.error ||
                err?.message ||
                'Failed to load templates';
            setNotificationMessage(msg);
            setOpenError(true);
            setWhatsAppTemplates([]);
            setShowTemplates(false);
        } finally {
            setOpenWait(false);
        }
    }, [getHeaders]);

    const MESSAGE_PAGE_SIZE = 50;

    const openConversation = async (contact, contact_number) => {
        setSelectedContact(contact);
        setActiveContactNum(contact_number);
        contactSetRef.current = contact_number;
        setShowTemplates(false);
        setUnreadCounts(prev => ({ ...prev, [contact_number]: 0 }));
        setMessagesLoading(true);
        setHasMoreMessages(false);
        oldestLoadedDateRef.current = null;

        try {
            await api.post('/whatsapp/markread', {
                contact_number,
                organization_id: currentUser?.organization_id
            }, { headers: getHeaders() });
        } catch (err) { console.error('Error marking messages as read:', err); }

        try {
            const res = await api.get('/whatsapp/getmessages/', {
                params: { contact_number, limit: MESSAGE_PAGE_SIZE },
                headers: getHeaders(),
            });
            const { messages: msgs = [], hasMore = false } = res.data || {};
            setWhatsAppMessagesData(msgs);
            setHasMoreMessages(hasMore);
            oldestLoadedDateRef.current = msgs[0]?.date || null;

            const agents = [...new Set(
                msgs.filter(m => m.origin === 'outbound')
                    .map(m => m.sender_name || m.agent_name || 'Agent').filter(Boolean)
            )];

            setConvStats({
                total: msgs.length,
                inbound: msgs.filter(m => m.origin === 'inbound').length,
                outbound: msgs.filter(m => m.origin === 'outbound').length,
                agents: agents.length > 0 ? agents : ['Agent'],
                firstMsg: msgs[0]?.date || null,
                isPartial: hasMore,
            });
        } catch (err) { console.error(err); }
        finally {
            setMessagesLoading(false);
            setTimeout(() => messageInputRef.current?.focus(), 100);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
        }
    };

    const loadOlderMessages = useCallback(async () => {
        if (!contactSetRef.current || !hasMoreMessages || loadingOlderMessages) return;
        if (!oldestLoadedDateRef.current) return;

        setLoadingOlderMessages(true);
        const container = messagesContainerRef.current;
        const prevScrollHeight = container ? container.scrollHeight : 0;

        try {
            const res = await api.get('/whatsapp/getmessages/', {
                params: {
                    contact_number: contactSetRef.current,
                    before: oldestLoadedDateRef.current,
                    limit: MESSAGE_PAGE_SIZE,
                },
                headers: getHeaders(),
            });
            const { messages: olderMsgs = [], hasMore = false } = res.data || {};

            if (olderMsgs.length > 0) {
                setWhatsAppMessagesData(prev => [...olderMsgs, ...prev]);
                oldestLoadedDateRef.current = olderMsgs[0]?.date || oldestLoadedDateRef.current;
            }
            setHasMoreMessages(hasMore);

            requestAnimationFrame(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - prevScrollHeight;
                }
            });
        } catch (err) {
            console.error('Error loading older messages:', err);
        } finally {
            setLoadingOlderMessages(false);
        }
    }, [hasMoreMessages, loadingOlderMessages, getHeaders]);

    const handleMessagesScroll = useCallback((e) => {
        if (e.target.scrollTop < 80 && hasMoreMessages && !loadingOlderMessages) {
            loadOlderMessages();
        }
    }, [hasMoreMessages, loadingOlderMessages, loadOlderMessages]);

    const handleFetchWhatsAppMessages = (contact, contact_number) =>
        openConversation(contact, contact_number);

    const handleFetchWhatsAppContactMessages = (name, rawPhoneNumber) => {
        setAddChatModal(false);
        setDirectNumber(''); setDirectNumberError(''); setDirectNumberPreview('');

        const { e164, valid } = normaliseKENumber(rawPhoneNumber);
        if (!valid) {
            setOpenError(true);
            setNotificationMessage(`"${name}" has an invalid phone number and can't be messaged on WhatsApp.`);
            return;
        }

        const contact = { contact_name: name, contact_number: e164, last_message_date: new Date().toISOString() };
        setSelectedContact(contact);
        setActiveContactNum(e164);
        contactSetRef.current = e164;
        setWhatsAppMessagesData([]);
        setConvStats(null);
        setTimeout(() => messageInputRef.current?.focus(), 100);
    };

    const handleDirectNumberChange = (e) => {
        const val = e.target.value;
        setDirectNumber(val);
        if (!val) { setDirectNumberError(''); setDirectNumberPreview(''); return; }
        const { display, valid } = normaliseKENumber(val);
        if (valid) { setDirectNumberError(''); setDirectNumberPreview(display); }
        else { setDirectNumberError('Enter a valid number (07xx, 254xx, or international)'); setDirectNumberPreview(''); }
    };

    const handleStartDirectChat = () => {
        if (!directNumber) return;
        const { e164, display, valid } = normaliseKENumber(directNumber);
        if (!valid) { setDirectNumberError('Invalid number'); return; }
        handleFetchWhatsAppContactMessages(display, e164);
    };

    const handleSendMessage = useCallback(async (event) => {
        event.preventDefault();
        if (selectedFileForUpload) {
            handleSendFile();
            return;
        }
        if (!contactSetRef.current || !newMessage.trim()) return;

        setIsSending(true);
        const msgText = newMessage.trim();
        setNewMessage('');
        setSelectedFileForUpload(null);

        try {
            const response = await api.post('/whatsapp/addmessage',
                {
                    contact_number: contactSetRef.current,
                    text_body: msgText,
                    type: 'text'
                },
                { headers: getHeaders(), withCredentials: true }
            );

            if (response.data?.success) {
                setOpenSuccess(true);
                setNotificationMessage('Message sent successfully!');
            }
        } catch (err) {
            console.error(err);
            setOpenError(true);
            setNotificationMessage('Failed to send message');
            setNewMessage(msgText);
        } finally {
            setIsSending(false);
        }
    }, [newMessage, getHeaders, selectedFileForUpload]);

    const handleSendTemplate = useCallback(async (template) => {
        if (!contactSetRef.current || !template) return;
        setShowTemplates(false);
        try {
            const lang =
                typeof template.language === 'string'
                    ? template.language
                    : (template.language?.code || 'en_US');

            await api.post('/whatsapp/addmessage',
                {
                    contact_number: contactSetRef.current,
                    text_body: `[Template: ${template.name}]`,
                    type: 'template',
                    template_name: template.name,
                    template_lang: lang,
                },
                { headers: getHeaders(), withCredentials: true }
            );
            setNotificationMessage(`Template "${template.name}" sent`);
            setOpenSuccess(true);
        } catch (err) {
            console.error(err);
            const details = err?.response?.data?.details;
            setNotificationMessage(
                (typeof details === 'string' ? details : details?.error?.message) ||
                err?.response?.data?.error ||
                'Failed to send template'
            );
            setOpenError(true);
        }
    }, [getHeaders]);

    const handleKeyPress = (e) => {
        if (e.key === 'enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const copyPhoneNumber = (number) =>
        window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: number }));

    const handleWhatsAppCallInvite = useCallback(async () => {
        if (!contactSetRef.current) return;
        setCallActionLoading(true);
        try {
            await api.post(
                '/whatsapp/calling/invite',
                {
                    contact_number: contactSetRef.current,
                    body_text: 'You can call us on WhatsApp for faster support.',
                    display_text: 'Call on WhatsApp',
                    ttl_minutes: 10080,
                },
                { headers: getHeaders() }
            );
            setNotificationMessage('WhatsApp call invite sent — customer can tap to call you');
            setOpenSuccess(true);
        } catch (err) {
            console.error(err);
            const details = err?.response?.data?.details;
            const hint = err?.response?.data?.hint;
            setNotificationMessage(
                (typeof details === 'string' ? details : details?.error?.message) ||
                err?.response?.data?.error ||
                hint ||
                'Failed to send call invite. Enable Calling on Meta and ensure an open 24h window.'
            );
            setOpenError(true);
        } finally {
            setCallActionLoading(false);
        }
    }, [getHeaders]);

    const handleWhatsAppCallPermission = useCallback(async () => {
        if (!contactSetRef.current) return;
        setCallActionLoading(true);
        try {
            await api.post(
                '/whatsapp/calling/permission',
                {
                    contact_number: contactSetRef.current,
                    body_text: 'Allow us to call you on WhatsApp about your request?',
                },
                { headers: getHeaders() }
            );
            setNotificationMessage('Call permission request sent');
            setOpenSuccess(true);
        } catch (err) {
            console.error(err);
            const details = err?.response?.data?.details;
            setNotificationMessage(
                (typeof details === 'string' ? details : details?.error?.message) ||
                err?.response?.data?.error ||
                'Failed to request call permission'
            );
            setOpenError(true);
        } finally {
            setCallActionLoading(false);
        }
    }, [getHeaders]);

    const fetchCallPermissionStatus = useCallback(async (contactNumber) => {
        if (!contactNumber) return;
        try {
            const res = await api.get('/whatsapp/calling/permission-status', {
                params: { contact_number: contactNumber },
                headers: getHeaders(),
            });
            setLastCallPermission({
                contact_number: contactNumber,
                ...(res.data || {}),
            });
        } catch (err) {
            console.warn('call permission status:', err?.response?.data || err.message);
        }
    }, [getHeaders]);

    const cancelFileSelection = () => {
        setSelectedFileForUpload(null);
        setNewMessage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── date helpers ──────────────────────────────────────────────────────────
    const formatMessageDate = (ds) => {
        const d = new Date(ds);
        if (!ds || isNaN(d)) return '';
        if (isToday(d)) return 'Today';
        if (isYesterday(d)) return 'Yesterday';
        if (differenceInDays(new Date(), d) < 7) return format(d, 'EEEE');
        return format(d, 'dd/MM/yyyy');
    };
    const formatTime = (ds) => {
        const d = new Date(ds);
        return (!ds || isNaN(d)) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    const showDateSep = (msgs, idx) => {
        if (idx === 0) return true;
        return new Date(msgs[idx].date).toDateString() !== new Date(msgs[idx - 1].date).toDateString();
    };

    const downloadMedia = useCallback(async (url, filename) => {
        try {
            const res = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Download failed:', err);
            setOpenError(true);
            setNotificationMessage('Failed to download file');
        }
    }, []);

    const dedupedContactPickerResults = useMemo(() => {
        const seen = new Set();
        return contactData.filter((c) => {
            const key = digitsOnly(c.phone_number);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [contactData]);

    // ══════════════════════════════════════════════════════════════════════════
    // Webchat helpers (live visitor chats)
    // ══════════════════════════════════════════════════════════════════════════
    const fetchWebchatSessionsRef = useRef(null);

    const fetchWebchatSessions = useCallback(async (term = '') => {
        setWebchatLoading(true);
        try {
            const res = await api.get('/webchat/getactivechats', {
                headers: getHeaders(),
                params: { search: term.trim() || undefined },
            });
            setWebchatSessions(res.data || []);
        } catch (err) { console.error(err); }
        finally { setWebchatLoading(false); }
    }, [getHeaders]);

    useEffect(() => { fetchWebchatSessionsRef.current = fetchWebchatSessions; }, [fetchWebchatSessions]);

    const debouncedWebchatSearch = useCallback(
        debounce((t) => fetchWebchatSessions(t), 300), [fetchWebchatSessions]
    );

    useEffect(() => { fetchWebchatSessions(''); }, [fetchWebchatSessions]);

    const openWebchatConversation = async (session) => {
        setSelectedWebchatSession(session);
        setWebchatMessages([]);
        setWebchatStatusText('');
        setWebchatVisitorTyping(false);
        clearTimeout(webchatTypingTimeoutRef.current);
        setWebchatUnreadCounts(prev => ({ ...prev, [session.session_id]: 0 }));
        setWebchatMessagesLoading(true);

        try {
            const res = await api.get('/webchat/getchatmessages', {
                params: { session_id: session.session_id },
                headers: getHeaders(),
            });
            setWebchatMessages(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setWebchatMessagesLoading(false);
            setTimeout(() => webchatMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
        }

        // Join the session room so we get typing indicators / status updates
        if (webchatSocketRef.current && webchatSocketRef.current.connected) {
            webchatSocketRef.current.emit('join_session', { session_id: session.session_id });
        }
    };

    const handleSendWebchatMessage = async (event) => {
        event.preventDefault();
        if (!selectedWebchatSession || !webchatNewMessage.trim()) return;

        const msgText = webchatNewMessage.trim();
        setWebchatSending(true);
        setWebchatNewMessage('');

        // Optimistic append
        setWebchatMessages(prev => [...prev, {
            origin: 'outbound',
            message: msgText,
            sender_name: currentUser?.user_name || 'Agent',
            date: new Date().toISOString(),
        }]);

        try {
            await api.post('/webchat/sendchatmessage', {
                session_id: selectedWebchatSession.session_id,
                message: msgText,
            }, { headers: getHeaders(), withCredentials: true });
        } catch (err) {
            console.error(err);
            setOpenError(true);
            setNotificationMessage('Failed to send message');
            setWebchatNewMessage(msgText);
        } finally {
            setWebchatSending(false);
        }
    };

    const handleWebchatKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendWebchatMessage(e);
        }
    };

    const handleEndWebchat = async (session_id) => {
        try {
            await api.post('/webchat/endchat', { session_id }, { headers: getHeaders(), withCredentials: true });
            setWebchatStatusText('Chat ended');
            setOpenSuccess(true);
            setNotificationMessage('Chat ended');
            fetchWebchatSessions(webchatSearchTerm);
            // Keep archived list fresh if user switches tabs
            if (activeWebchatMiniTab === 1) fetchArchivedSessions();
            if (selectedWebchatSession?.session_id === session_id) {
                setSelectedWebchatSession(null);
                setWebchatMessages([]);
                setWebchatVisitorTyping(false);
                clearTimeout(webchatTypingTimeoutRef.current);
            }
        } catch (err) {
            console.error(err);
            setOpenError(true);
            setNotificationMessage('Failed to end chat');
        }
    };

    const webchatFormatTime = (ds) => {
        const d = new Date(ds);
        return (!ds || isNaN(d)) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    const webchatFormatDate = (ds) => {
        const d = new Date(ds);
        if (!ds || isNaN(d)) return '';
        if (isToday(d)) return 'Today';
        if (isYesterday(d)) return 'Yesterday';
        if (differenceInDays(new Date(), d) < 7) return format(d, 'EEEE');
        return format(d, 'dd/MM/yyyy');
    };
    const webchatShowDateSep = (msgs, idx) => {
        if (idx === 0) return true;
        return new Date(msgs[idx].date).toDateString() !== new Date(msgs[idx - 1].date).toDateString();
    };

    // ── Archived (ended) chats ────────────────────────────────────────────────
    const fetchArchivedSessions = useCallback(async (term = archivedSearchTerm, page = archivedPage) => {
        setArchivedLoading(true);
        try {
            const res = await api.get('/webchat/getendedchats', {
                headers: getHeaders(),
                params: {
                    search: (term || '').trim() || undefined,
                    page: page + 1, // backend is 1-indexed
                    limit: archivedRowsPerPage,
                },
            });
            setArchivedSessions(res.data?.data || []);
            setTotalArchivedSessions(res.data?.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setArchivedLoading(false);
        }
    }, [getHeaders, archivedPage, archivedRowsPerPage, archivedSearchTerm]);

    const debouncedArchivedSearch = useCallback(
        debounce((t) => {
            setArchivedSearchTerm(t);
            setArchivedPage(0);
        }, 300),
        []
    );

    useEffect(() => {
        if (activeTab === 5 && activeWebchatMiniTab === 1) {
            fetchArchivedSessions();
        }
    }, [activeTab, activeWebchatMiniTab, fetchArchivedSessions]);

    // ── Offline leads ─────────────────────────────────────────────────────────
    const fetchOfflineLeads = useCallback(async () => {
        try {
            const res = await api.get('/webchat/getofflineleads', {
                params: {
                    search: offlineLeadsSearch,
                    limit: offlineLeadsRowsPerPage,
                    page: offlineLeadsPage + 1, // backend is 1-indexed: offset = (page-1)*limit
                },
                headers: getHeaders(),
            });
            setOfflineLeads(res.data?.data || []);
            setTotalOfflineLeads(res.data?.total || 0);
        } catch (err) { console.error(err); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offlineLeadsSearch, offlineLeadsPage, offlineLeadsRowsPerPage, getHeaders]);

    useEffect(() => {
        if (activeTab === 5 && activeWebchatMiniTab === 2) fetchOfflineLeads();
    }, [activeTab, activeWebchatMiniTab, fetchOfflineLeads]);

    // ══════════════════════════════════════════════════════════════════════════
    // SMS helpers
    // ══════════════════════════════════════════════════════════════════════════
    const fetchOutboxData = async () => {
        try {
            const r = await api.get('/sms/getsms', {
                params: { search: searchTerm, start_date: startDate, end_date: endDate, limit: rowsPerPage, offset: page * rowsPerPage },
                headers: getHeaders()
            });
            setData(r.data.data);
            setTotalItems(r.data.total);
        } catch(e){console.error(e);}
    };

    const fetchTemplateData = async () => {
        try {
            const r = await api.get('/sms/getsmstemplates', {
                params: { search: searchTemplateTerm, start_date: startDate, end_date: endDate, limit: templaterowsPerPage, offset: templatepage * templaterowsPerPage },
                headers: getHeaders()
            });
            setTemplateData(r.data.data);
            setTotalTemplateItems(r.data.total);
        } catch(e){console.error(e);}
    };

    const fetchTemplateList = async () => {
        try {
            const r = await api.get('sms/getsmstemplatelist', { headers: getHeaders() });
            setTemplateListData(r.data);
        } catch(e){console.error(e);}
    };

    const fetchContactCampaignData = async () => {
        try {
            const r = await api.get('/sms/getcontactcampaignnames', {
                params: { search: searchTemplateTerm, start_date: startDate, end_date: endDate, limit: campaignrowsPerPage, offset: campaignpage * campaignrowsPerPage },
                headers: getHeaders()
            });
            setContactCampaignData(r.data.data);
            setTotalContactCampaignItems(r.data.total);
        } catch(e){console.error(e);}
    };

    const fetchCampaignContactList = async () => {
        try {
            const r = await api.get('sms/getsmscontactcampaignselect/', { headers: getHeaders() });
            setCampaignContactList(r.data);
        } catch(e){console.error(e);}
    };

    const fetchContactData = async () => {
        try {
            const r = await api.get('/contact/all', {
                params: { search: searchContactTerm, start_date: startDate, end_date: endDate, limit: contactrowsPerPage, offset: contactpage * contactrowsPerPage },
                headers: getHeaders()
            });
            setContactData(r.data.data);
            setTotalContactItems(r.data.total);
        } catch(e){console.error(e);}
    };

    const fetchSingleContactCampaignData = async (id) => {
        try {
            const r = await api.get('/sms/getcampaigncontact/', {
                params: { campaign_id: id, limit: campaignContactrowsPerPage, offset: campaignContactpage * campaignContactrowsPerPage },
                headers: getHeaders()
            });
            setCampaignData(r.data.data);
            setTotalCampaignContactItems(r.data.total);
        } catch(e){console.error(e);}
    };

    const fetchTemplateListContent = async (e) => {
        const id = e.target.value;
        setSelectedTemplateId(id);
        try {
            const r = await api.get('/sms/getsmstemplatelistcontent/', {
                params: { id },
                headers: getHeaders()
            });
            setTempContentValue(r.data.content);
        } catch(e){console.error(e);}
    };

    useEffect(() => { fetchContactData(); }, [searchContactTerm, contactpage, contactrowsPerPage]);
    useEffect(() => {
        fetchTemplateData();
        fetchTemplateList();
        fetchOutboxData();
        fetchContactCampaignData();
        fetchCampaignContactList();
    }, [searchTemplateTerm, startDate, endDate, templatepage, templaterowsPerPage, searchTerm, page, rowsPerPage]);

    const handleSMSSubmit = async (event) => {
        event.preventDefault();
        setOpenWait(true);
        const fd = new FormData(event.target);
        const cn = fd.get('campaign_name');
        const msg = fd.get('message');
        const rawPhone = fd.get('phone_number');
        try {
            const headers = getHeaders();
            const r = cn
                ? await api.post('/sms/sendsmscampaign', { campaign_name: cn, message: msg }, { withCredentials: true, headers })
                : await api.post('/sms/sendsms', {
                      mobile: [normaliseKENumber(rawPhone).e164 || rawPhone],
                      message: msg
                  }, { withCredentials: true, headers });
 
            setOpenWait(false);
            const { successful_sends = [], failed_sends = [] } = r.data || {};
            const sentCount = successful_sends.length;
            const failedCount = failed_sends.length;
            if (sentCount > 0 && failedCount === 0) {
                setOpenSuccess(true);
                setNotificationMessage(cn ? `Campaign sent: ${sentCount} message(s) delivered.` : 'SMS sent successfully!');
            } else if (sentCount > 0 && failedCount > 0) {
                setOpenSuccess(true);
                setNotificationMessage(`Sent ${sentCount}, but ${failedCount} failed. Check Outbox for details.`);
            } else {
                setOpenError(true);
                const firstError = typeof failed_sends[0] === 'object' ? failed_sends[0]?.error : null;
                setNotificationMessage(firstError || 'SMS failed to send. Please check your SMS provider settings.');
            }
 
            document.getElementById('contact-form')?.reset();
            setContactMethod('');
            setPhoneNumber('');
            setTempContentValue('');
            setSelectedTemplateId('');
            fetchOutboxData();
        } catch (e) {
            console.error('SMS send error:', e);
            setOpenWait(false);
            setOpenError(true);
            setNotificationMessage(e.response?.data?.error || 'Failed to send SMS. Please try again.');
        }
    };

    const handleTemplateSubmit = (e) => {
        e.preventDefault();
        api.post('/sms/addsmstemplate', new FormData(e.target), { headers: getHeaders(), withCredentials: true })
            .then(() => { fetchTemplateData(); fetchTemplateList(); setAddTemplateModal(false); })
            .catch(console.error);
    };

    const handleTemplateEditSubmit = (e) => {
        e.preventDefault();
        api.post('/sms/editsmstemplate', new FormData(e.target), { headers: getHeaders(), withCredentials: true })
            .then(() => { fetchTemplateData(); fetchTemplateList(); setEditTemplateModal(false); })
            .catch(console.error);
    };

    const handleTemplateDelete = (id) =>
        api.delete(`/sms/deletesmstemplate/${id}`, { headers: getHeaders(), withCredentials: true })
            .then(() => { fetchTemplateData(); fetchTemplateList(); setEditTemplateModal(false); })
            .catch(console.error);

    const handleDeleteCampaignContacts = (id) =>
        api.delete(`/sms/deletecampaigncontacts/${id}`, { headers: getHeaders(), withCredentials: true })
            .then(() => { setContactConfirmModal(false); fetchContactCampaignData(); fetchCampaignContactList(); })
            .catch(console.error);

    const handleCampaignMessageUpdate = (e) => {
        e.preventDefault();
        api.post('/sms/editsmscampaignmessage/', new FormData(e.target), { headers: getHeaders(), withCredentials: true })
            .then(() => { setContactMessageModal(false); fetchContactCampaignData(); })
            .catch(console.error);
    };

    const deleteCampaignContact = (id, cid) =>
        api.delete(`/sms/deletecampaigncontact/${id}`, { headers: getHeaders(), withCredentials: true })
            .then(() => fetchSingleContactCampaignData(cid))
            .catch(console.error);

    const handleBulkContactSubmit = async (e) => {
        e.preventDefault();
        if (!selectedFile) return;
        const fd = new FormData();
        fd.append('file', selectedFile);
        fd.append('campaignName', campaignName);
        setEnableDisableButton('disabled');
        setUploadButtonStatus('Uploading… Please Wait');
        try {
            await api.post('/sms/addcampaigncontacts/', fd, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(authToken)}`,
                },
                withCredentials: true
            });
            setBulkContactModal(false);
            setEnableDisableButton('');
            setUploadButtonStatus('Submit');
            fetchContactCampaignData();
            fetchCampaignContactList();
        } catch(e){console.error(e);}
    };

    const handleInputModChange = (e) => {
        const { name, value } = e.target;
        setSelectedItem(prev => ({ ...prev, [name]: value }));
    };

    const downloadTemplate = () => {
        const a = document.createElement('a');
        a.href = '/campaign_template.csv';
        a.download = 'campaign_template.csv';
        a.click();
    };

    // ══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ══════════════════════════════════════════════════════════════════════════
    return (
        <>
            <CssBaseline />

            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={{ mb: 2 }}>
                <Tab
                    label={
                        <Badge
                            badgeContent={whatsAppUnreadCount}
                            color="error"
                            invisible={whatsAppUnreadCount === 0}
                            sx={{ '& .MuiBadge-badge': { top: -5, right: -15 } }}
                        >
                            WhatsApp
                        </Badge>
                    }
                />
                <Tab label="SMS" />
                <Tab label="Facebook" />
                <Tab label="Instagram" />
                <Tab label="Twitter" />
                <Tab
                    label={
                        <Badge
                            badgeContent={Object.values(webchatUnreadCounts).reduce((a, b) => a + b, 0)}
                            color="error"
                            invisible={Object.values(webchatUnreadCounts).reduce((a, b) => a + b, 0) === 0}
                            sx={{ '& .MuiBadge-badge': { top: -5, right: -15 } }}
                        >
                            Webchat
                        </Badge>
                    }
                />
            </Tabs>

            {/* ════ WHATSAPP ════ */}
            {activeTab === 0 && (
              <Box sx={{ position: 'relative' }}>
                <Grid container spacing={0}>
                    {/* Left: contacts */}
                    <Grid item xs={3}>
                        <Box sx={{
                            height: '77vh',
                            bgcolor: 'background.paper',
                            border: '0.1px solid rgba(207,211,212,0.2)',
                            borderRadius: '5px 0 0 5px',
                            p: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={5}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="success"
                                        size="small"
                                        sx={{ fontWeight:'bold' }}
                                        onClick={() => setAddChatModal(true)}
                                    >
                                        + New Chat
                                    </Button>
                                </Grid>
                                <Grid item xs={5}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="info"
                                        size="small"
                                        sx={{ fontWeight:'bold' }}
                                        onClick={fetchWhatsAppTemplates}
                                    >
                                        Templates
                                    </Button>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign:'right' }}>
                                    <Tooltip title={socket?.connected ? 'Connected' : (isReconnecting ? 'Reconnecting…' : 'Disconnected — click to reconnect')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handleManualReconnect}
                                                disabled={socket?.connected || isReconnecting}
                                                sx={{ p: 0.5 }}
                                            >
                                                {isReconnecting
                                                    ? <Sync sx={{ color:'warning.main', fontSize:'1.2rem', animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                                                    : socket?.connected
                                                        ? <Wifi sx={{ color:'success.main', fontSize:'1.2rem' }} />
                                                        : <WifiOff sx={{ color:'error.main', fontSize:'1.2rem' }} />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            <TextField
                                fullWidth
                                size="small"
                                label="Search contacts"
                                value={searchWhatsAppTerm}
                                onChange={(e) => {
                                    setSearchWhatsAppTerm(e.target.value);
                                    debouncedContactSearch(e.target.value);
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize:'1rem' }} /></InputAdornment>,
                                    endAdornment: searchWhatsAppTerm ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => {
                                                setSearchWhatsAppTerm('');
                                                fetchWhatsAppContacts('');
                                            }}>
                                                <Close sx={{ fontSize:'1rem' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            <Box sx={{ flex:1, overflowY:'auto', pr:'2px' }}>
                                {loading && <Box sx={{ textAlign:'center',mt:2 }}><CircularProgress size={24} /></Box>}
                                {!loading && whatsAppContacts.length === 0 && (
                                    <Typography variant="body2" sx={{ textAlign:'center',mt:3,color:'text.secondary' }}>
                                        No conversations yet
                                    </Typography>
                                )}
                                {whatsAppContacts.map((contact) => {
                                    const unread = unreadCounts[contact.contact_number] || 0;
                                    const name = contact.contact_name || contact.contact_number;
                                    const isActive = activeContactNum === contact.contact_number;
                                    const messageType = contact.last_message_type || 'text';
                                    const typeIcon = getMessageTypeLabel(messageType);

                                    let lastMessageFrom = '';
                                    if (contact.last_message_origin === 'inbound') {
                                        lastMessageFrom = contact.contact_name || 'Unknown';
                                    } else if (contact.last_message_origin === 'outbound') {
                                        lastMessageFrom = contact.last_sender_name || 'You';
                                    }

                                    return (
                                        <Box
                                            key={contact.contact_number}
                                            onClick={() => handleFetchWhatsAppMessages(contact, contact.contact_number)}
                                            sx={{
                                                cursor:'pointer',
                                                bgcolor: isActive ? 'action.selected' : 'transparent',
                                                p:'8px 6px',
                                                borderRadius:'8px',
                                                mt:'4px',
                                                display:'flex',
                                                alignItems:'center',
                                                gap:1,
                                                '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
                                                transition:'background 0.15s'
                                            }}
                                        >
                                            <Badge badgeContent={unread} color="success" invisible={unread === 0}>
                                                <Avatar sx={{ width:38, height:38, bgcolor: avatarColor(name), fontSize:'0.8rem' }}>
                                                    {initials(name)}
                                                </Avatar>
                                            </Badge>
                                            <Box sx={{ flex:1, minWidth:0 }}>
                                                <Box sx={{
                                                    fontWeight: unread > 0 ? 700 : 500,
                                                    fontSize:'0.875rem',
                                                    whiteSpace:'nowrap',
                                                    overflow:'hidden',
                                                    textOverflow:'ellipsis'
                                                }}>
                                                    {name}
                                                </Box>
                                                <Box sx={{
                                                    fontSize:'0.75rem',
                                                    color:'text.secondary',
                                                    whiteSpace:'nowrap',
                                                    overflow:'hidden',
                                                    textOverflow:'ellipsis'
                                                }}>
                                                    {lastMessageFrom && <strong>{lastMessageFrom}: </strong>}
                                                    {typeIcon} {contact.last_message_text ? `${contact.last_message_text.slice(0,28)}…` : ''}
                                                </Box>
                                            </Box>
                                            <Box sx={{ fontSize:'0.65rem', color:'text.secondary', whiteSpace:'nowrap', flexShrink:0 }}>
                                                {formatTime(contact.last_message_date)}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Middle: chat */}
                    <Grid item xs={6}>
                        <Box sx={{
                            bgcolor:'background.paper',
                            border:'0.1px solid rgba(207,211,212,0.2)',
                            p:'10px',
                            height:'77vh',
                            display:'flex',
                            flexDirection:'column'
                        }}>
                            <Box sx={{
                                display:'flex',
                                justifyContent:'space-between',
                                alignItems:'center',
                                mb:1,
                                pb:1,
                                borderBottom:'1px solid rgba(207,211,212,0.2)'
                            }}>
                                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                                    {selectedContact && (
                                        <Avatar sx={{
                                            width:32,
                                            height:32,
                                            bgcolor: avatarColor(selectedContact.contact_name || selectedContact.contact_number),
                                            fontSize:'0.75rem'
                                        }}>
                                            {initials(selectedContact.contact_name || selectedContact.contact_number || '')}
                                        </Avatar>
                                    )}
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight:600, lineHeight:1.2 }}>
                                            {selectedContact ? (selectedContact.contact_name || selectedContact.contact_number) : 'Select a contact'}
                                        </Typography>
                                        {selectedContact?.contact_name && (
                                            <Typography variant="caption" sx={{ color:'text.secondary' }}>
                                                {selectedContact.contact_number}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {activeContactNum && (
                                    <WhatsAppCallActions
                                        contactNumber={activeContactNum}
                                        getHeaders={getHeaders}
                                        disabled={isSending || callActionLoading}
                                        onNotify={({ severity, message }) => {
                                            setNotificationMessage(message);
                                            if (severity === 'success') setOpenSuccess(true);
                                            else setOpenError(true);
                                        }}
                                    />
                                )}
                            </Box>

                            <Box
                                ref={messagesContainerRef}
                                onScroll={handleMessagesScroll}
                                sx={{ flex:1, overflowY:'auto', p:'8px' }}
                            >
                                {loadingOlderMessages && (
                                    <Box sx={{ display:'flex', justifyContent:'center', py:1 }}>
                                        <CircularProgress size={18} />
                                    </Box>
                                )}
                                {messagesLoading && (
                                    <Box sx={{ display:'flex', justifyContent:'center', mt:4 }}>
                                        <CircularProgress size={32} />
                                    </Box>
                                )}
                                {!messagesLoading && !selectedContact && (
                                    <Box sx={{
                                        display:'flex',
                                        flexDirection:'column',
                                        alignItems:'center',
                                        justifyContent:'center',
                                        height:'100%',
                                        color:'text.secondary'
                                    }}>
                                        <Person sx={{ fontSize:'4rem', opacity:0.3 }} />
                                        <Typography variant="body2" sx={{ mt:1 }}>Select a contact to start chatting</Typography>
                                    </Box>
                                )}
                                {!messagesLoading && selectedContact && whatsAppMessagesData.length === 0 && (
                                    <Box sx={{ textAlign:'center', mt:4, color:'text.secondary' }}>
                                        <Typography variant="body2">No messages yet. Send the first message!</Typography>
                                    </Box>
                                )}

                                {!messagesLoading && whatsAppMessagesData.map((msg, idx) => {
                                    const out = msg.origin === 'outbound';
                                    const msgType = msg.type || 'text';
                                    const isMedia = ['image', 'video', 'audio', 'document'].includes(msgType);

                                    let senderDisplayName = '';
                                    if (out) {
                                        senderDisplayName = msg.sender_name || 'Agent';
                                    } else {
                                        senderDisplayName = selectedContact?.contact_name || msg.sender_name || 'Unknown';
                                    }

                                    return (
                                        <Box key={idx}>
                                            {showDateSep(whatsAppMessagesData, idx) && (
                                                <Box sx={{ display:'flex', alignItems:'center', my:2, gap:1 }}>
                                                    <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                                                    <Typography variant="caption" sx={{ color:'text.secondary', px:1, whiteSpace:'nowrap' }}>
                                                        {formatMessageDate(msg.date)}
                                                    </Typography>
                                                    <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                                                </Box>
                                            )}
                                            <Box
                                                ref={idx === whatsAppMessagesData.length - 1 ? messagesEndRef : null}
                                                sx={{
                                                    display:'flex',
                                                    justifyContent: out ? 'flex-end' : 'flex-start',
                                                    mb:1,
                                                    alignItems:'flex-end',
                                                    gap:0.5
                                                }}
                                            >
                                                {!out && (
                                                    <Avatar sx={{
                                                        width:24,
                                                        height:24,
                                                        bgcolor: avatarColor(selectedContact?.contact_name||''),
                                                        fontSize:'0.6rem',
                                                        flexShrink:0
                                                    }}>
                                                        {initials(selectedContact?.contact_name || selectedContact?.contact_number || '?')}
                                                    </Avatar>
                                                )}
                                                <Box sx={{ maxWidth:'70%' }}>
                                                    <Typography variant="caption" sx={{
                                                        display:'block',
                                                        textAlign: out ? 'right' : 'left',
                                                        color:'text.secondary',
                                                        mb:'2px',
                                                        fontSize:'0.65rem',
                                                        fontWeight: out ? 500 : 400
                                                    }}>
                                                        {senderDisplayName}
                                                    </Typography>
                                                    <Box sx={{
                                                        bgcolor: out ? 'primary.main' : 'background.chatrecipient',
                                                        color: out ? 'primary.contrastText' : 'inherit',
                                                        borderRadius: out ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                                        p:'8px 12px',
                                                        wordBreak:'break-word',
                                                        maxWidth: '100%',
                                                    }}>
                                                        {isMedia && msg.media_url && (
                                                            <Box sx={{ mb: 1 }}>
                                                                {msgType === 'image' && (
                                                                    <Box sx={{ position: 'relative', cursor: 'pointer' }}
                                                                         onClick={() => setLightboxMedia({ url: msg.media_url, name: msg.file_name || msg.text_body || 'image.jpg', type: 'image' })}>
                                                                        <img
                                                                            src={msg.media_url}
                                                                            alt={msg.text_body || 'Image'}
                                                                            style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px', objectFit: 'cover', display: 'block' }}
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                                                            onClick={(e) => { e.stopPropagation(); setLightboxMedia({ url: msg.media_url, name: msg.file_name || msg.text_body || 'image.jpg', type: 'image' }); }}
                                                                        >
                                                                            <Visibility sx={{ fontSize: '0.8rem' }} />
                                                                        </IconButton>
                                                                    </Box>
                                                                )}
                                                                {msgType === 'video' && (
                                                                    <Box sx={{ position: 'relative' }}>
                                                                        <video src={msg.media_url} controls style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px', display: 'block' }} />
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                                                            onClick={(e) => { e.stopPropagation(); setLightboxMedia({ url: msg.media_url, name: msg.file_name || msg.text_body || 'video.mp4', type: 'video' }); }}
                                                                        >
                                                                            <Visibility sx={{ fontSize: '0.8rem' }} />
                                                                        </IconButton>
                                                                    </Box>
                                                                )}
                                                                {msgType === 'audio' && (
                                                                    <audio src={msg.media_url} controls style={{ width: '100%' }} />
                                                                )}
                                                                {(msgType === 'document' || msg.file_name) && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', p: 1, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                                                                        <AttachFile sx={{ fontSize: '1.2rem' }} />
                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{msg.file_name || msg.text_body || 'Document'}</Typography>
                                                                            {msg.file_size && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatFileSize(msg.file_size)}</Typography>}
                                                                        </Box>
                                                                        {msg.media_url && (
                                                                            <IconButton size="small" onClick={() => downloadMedia(msg.media_url, msg.file_name || 'document')}>
                                                                                <Download sx={{ fontSize: '1rem' }} />
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}

                                                        {isCallTimelineType(msgType) ? (
                                                            <Box sx={{
                                                                display: 'flex', alignItems: 'center', gap: 1,
                                                                py: 0.5, px: 0.5,
                                                            }}>
                                                                <Box sx={{
                                                                    width: 32, height: 32, borderRadius: '50%',
                                                                    bgcolor: msg.origin === 'outbound' ? 'primary.main' : 'success.main',
                                                                    color: '#fff', display: 'flex', alignItems: 'center',
                                                                    justifyContent: 'center', flexShrink: 0, fontSize: '0.95rem',
                                                                }}>
                                                                    {msgType.includes('permission') ? '🔑' : '📞'}
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {msg.text_body || (msgType.includes('permission') ? 'Call permission' : 'WhatsApp call')}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                                        {msg.origin === 'outbound' ? 'You' : 'Customer'} · {msgType.replace(/_/g, ' ')}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            msg.text_body && (
                                                                <Typography variant="body2">{msg.text_body}</Typography>
                                                            )
                                                        )}

                                                        <Typography variant="caption" sx={{ display:'block', textAlign:'right', mt:'2px', opacity:0.75, fontSize:'0.65rem' }}>
                                                            {formatTime(msg.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {showTemplates && (
                                <Box sx={{ mb:1, p:'6px', bgcolor:'action.hover', borderRadius:'8px', display:'flex', gap:1, flexWrap:'wrap', maxHeight:'90px', overflowY:'auto' }}>
                                    <Typography variant="caption" sx={{ width:'100%', color:'text.secondary', mb:'2px' }}>
                                        {whatsAppTemplates.length ? 'Select a template:' : 'No approved templates loaded'}
                                    </Typography>
                                    {whatsAppTemplates.map((tpl) => (
                                        <Chip
                                            key={tpl.id || tpl.name}
                                            label={tpl.name}
                                            onClick={() => handleSendTemplate(tpl)}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            sx={{ cursor:'pointer', fontSize:'0.75rem' }}
                                        />
                                    ))}
                                    <Chip label="✕ Close" size="small" onClick={() => setShowTemplates(false)} sx={{ cursor:'pointer' }} />
                                </Box>
                            )}

                            {activeContactNum && (
                                <Paper elevation={2} sx={{ p: '8px 12px', borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'relative' }}>
                                    {showEmojiPicker && (
                                        <Box sx={{ position: 'absolute', bottom: '100%', right: 0, zIndex: 1000 }}>
                                            <EmojiPicker onEmojiClick={onEmojiClick} />
                                        </Box>
                                    )}
                                    <form onSubmit={handleSendMessage} style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                        <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                            <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ color: 'text.secondary' }} disabled={isSending}>
                                                <EmojiEmotions />
                                            </IconButton>
                                            <Tooltip title="Images ≤5MB · Videos & Audio ≤16MB · Documents ≤100MB">
                                                <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ color: 'text.secondary' }} disabled={isSending}>
                                                    <AttachFile />
                                                </IconButton>
                                            </Tooltip>
                                            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx" disabled={isSending} />
                                        </Box>
                                        <TextField
                                            inputRef={messageInputRef}
                                            multiline maxRows={4}
                                            placeholder="Type a message..."
                                            fullWidth variant="standard"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            disabled={isSending}
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ '& .MuiInputBase-root': { py: '4px', fontSize: '0.95rem' } }}
                                        />
                                        <Button type="submit" variant="contained" disabled={(!newMessage.trim() && !selectedFileForUpload) || isSending}
                                            sx={{ minWidth: '45px', height: '45px', borderRadius: '50%', p: 0, bgcolor: (newMessage.trim() || selectedFileForUpload) ? 'primary.main' : 'action.disabledBackground' }}>
                                            {isSending ? <CircularProgress size={20} color="inherit" /> : <Send sx={{ fontSize: '20px' }} />}
                                        </Button>
                                    </form>

                                    {isUploading && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress variant="determinate" value={uploadProgress} />
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading: {uploadProgress}%</Typography>
                                        </Box>
                                    )}
                                    {selectedFileForUpload && !isUploading && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: '4px 8px', bgcolor: 'action.hover', borderRadius: '8px' }}>
                                            <Typography variant="caption">📎 {selectedFileForUpload.name} ({(selectedFileForUpload.size / 1024).toFixed(1)} KB)</Typography>
                                            <IconButton size="small" onClick={cancelFileSelection} disabled={isSending}><Close sx={{ fontSize: '1rem' }} /></IconButton>
                                        </Box>
                                    )}
                                </Paper>
                            )}
                        </Box>
                    </Grid>

                    {/* Right: info panel */}
                    <Grid item xs={3}>
                        <Box sx={{ height:'77vh', bgcolor:'background.paper', border:'0.1px solid rgba(207,211,212,0.2)', borderRadius:'0 5px 5px 0', p:'10px', overflowY:'auto' }}>
                            {selectedContact ? (<>
                                <Box sx={{ textAlign:'center', mb:2 }}>
                                    <Avatar sx={{ width:56, height:56, mx:'auto', mb:1, bgcolor: avatarColor(selectedContact.contact_name||selectedContact.contact_number), fontSize:'1.2rem' }}>
                                        {initials(selectedContact.contact_name||selectedContact.contact_number||'')}
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight:600 }}>{selectedContact.contact_name || selectedContact.contact_number}</Typography>
                                    {selectedContact.contact_name && (
                                        <Typography variant="caption" sx={{ color:'text.secondary' }}>{selectedContact.contact_number}</Typography>
                                    )}
                                </Box>

                                <Divider sx={{ mb:2 }} />
                                <Typography variant="overline" sx={{ color:'text.secondary', fontSize:'0.65rem' }}>Contact</Typography>
                                <Typography variant="body2" sx={{ mb:0.5 }}><strong>Phone:</strong> {selectedContact.contact_number}</Typography>
                                <Typography variant="body2" sx={{ mb:2 }}>
                                    <strong>Last Active:</strong> {selectedContact.last_message_date ? `${formatMessageDate(selectedContact.last_message_date)}, ${formatTime(selectedContact.last_message_date)}` : '—'}
                                </Typography>
                                {lastCallPermission && digitsOnly(lastCallPermission.contact_number) === digitsOnly(selectedContact.contact_number) && (
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        <strong>WhatsApp call permission:</strong>{' '}
                                        {lastCallPermission.response || lastCallPermission.status || '—'}
                                        {lastCallPermission.is_permanent ? ' (permanent)' : ''}
                                        {lastCallPermission.expiration_timestamp
                                            ? ` · expires ${new Date(Number(lastCallPermission.expiration_timestamp) * 1000).toLocaleString()}`
                                            : ''}
                                    </Typography>
                                )}

                                <Divider sx={{ mb:2 }} />
                                <Typography variant="overline" sx={{ color:'text.secondary', fontSize:'0.65rem' }}>Conversation Stats</Typography>
                                {convStats ? (<>
                                    {convStats.isPartial && (
                                        <Typography variant="caption" sx={{ display:'block', color:'text.secondary', mb:0.5, fontStyle:'italic' }}>Showing recent messages — scroll up in chat for more</Typography>
                                    )}
                                    <Grid container spacing={1} sx={{ mb:1, mt:0.5 }}>
                                        {[
                                            [convStats.isPartial ? 'Loaded' : 'Total', convStats.total, 'action.hover'],
                                            ['Received', convStats.inbound, 'success.light'],
                                            ['Sent', convStats.outbound, 'action.hover']
                                        ].map(([label, val, bg]) => (
                                            <Grid item xs={4} key={label}>
                                                <Box sx={{ textAlign:'center', bgcolor: bg, borderRadius:'6px', p:'6px' }}>
                                                    <Typography variant="h6" sx={{ fontSize:'1rem', fontWeight:700 }}>{val}</Typography>
                                                    <Typography variant="caption" sx={{ fontSize:'0.6rem', color:'text.secondary' }}>{label}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {convStats.firstMsg && (
                                        <Typography variant="body2"><strong>{convStats.isPartial ? 'Oldest loaded' : 'First message'}:</strong> {formatMessageDate(convStats.firstMsg)}</Typography>
                                    )}
                                    {convStats.senders && convStats.senders.length > 0 && (
                                        <Typography variant="body2" sx={{ mt:1 }}><strong>Agents:</strong> {convStats.senders.join(', ')}</Typography>
                                    )}
                                </>) : (
                                    <Typography variant="body2" sx={{ color:'text.secondary' }}>No conversation yet</Typography>
                                )}

                                <Divider sx={{ my:2 }} />
                                <Typography variant="overline" sx={{ color:'text.secondary', fontSize:'0.65rem' }}>Agents in Conversation</Typography>
                                {convStats?.agents?.length > 0 ? (
                                    <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, mt:1 }}>
                                        {convStats.agents.map((agent, i) => (
                                            <Chip key={i} label={agent} size="small" avatar={<Avatar sx={{ bgcolor: avatarColor(agent), fontSize:'0.6rem !important' }}>{initials(agent)}</Avatar>} sx={{ fontSize:'0.7rem' }} />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>No agent replies yet</Typography>
                                )}
                            </>) : (
                                <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:'text.secondary' }}>
                                    <Person sx={{ fontSize:'3rem', opacity:0.3 }} />
                                    <Typography variant="body2" sx={{ mt:1 }}>No contact selected</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* New Chat modal */}
                    <Modal open={openAddChatModal} onClose={() => { setAddChatModal(false); setDirectNumber(''); setDirectNumberError(''); setDirectNumberPreview(''); }}>
                        <Box sx={modalStyle}>
                            <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                <h3>New Chat</h3>
                                <Cancel onClick={() => { setAddChatModal(false); setDirectNumber(''); setDirectNumberError(''); setDirectNumberPreview(''); }} sx={{ cursor:'pointer' }} />
                            </Box>
                            <Box sx={{ px:1, pb:0 }}>
                                <Typography variant="subtitle2" sx={{ mb:1 }}>Type a number directly:</Typography>
                                <Box sx={{ display:'flex', gap:1, alignItems:'flex-start' }}>
                                    <TextField
                                        fullWidth size="small" label="07xx / 254xx / +254xx / international"
                                        value={directNumber} onChange={handleDirectNumberChange}
                                        error={!!directNumberError} helperText={directNumberError || (directNumberPreview ? `Will send to: ${directNumberPreview}` : ' ')}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleStartDirectChat(); } }}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize:'1rem' }} /></InputAdornment> }}
                                    />
                                    <Button variant="contained" color="success" disabled={!directNumber || !!directNumberError || !directNumberPreview} onClick={handleStartDirectChat} sx={{ whiteSpace:'nowrap', height:'40px', flexShrink:0 }}>
                                        Start Chat
                                    </Button>
                                </Box>
                            </Box>
                            <Divider sx={{ my:2 }}><Typography variant="caption" sx={{ color:'text.secondary' }}>OR PICK FROM CONTACTS</Typography></Divider>
                            <Box sx={{ px:1, mb:1 }}>
                                <TextField fullWidth size="small" label="Search contacts" value={searchContactTerm} onChange={(e) => setSearchContactTerm(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize:'1rem' }} /></InputAdornment> }} />
                            </Box>
                            <TableContainer sx={{ maxHeight:'250px' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Phone</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dedupedContactPickerResults.length > 0 ? dedupedContactPickerResults.map((r) => (
                                            <TableRow key={r.id} onClick={() => handleFetchWhatsAppContactMessages(r.name, r.phone_number)} sx={{ cursor:'pointer', '&:hover': { bgcolor:'action.hover' } }}>
                                                <TableCell>
                                                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                                                        <Avatar sx={{ width:24, height:24, bgcolor: avatarColor(r.name), fontSize:'0.6rem' }}>{initials(r.name)}</Avatar>
                                                        {r.name}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{r.phone_number}</TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow><TableCell colSpan={2} align="center">No contacts found</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination component="div" count={totalContactItems} page={contactpage} onPageChange={(_, v) => setContactPage(v)} rowsPerPage={contactrowsPerPage}
                                onRowsPerPageChange={(e) => { setContactRowsPerPage(parseInt(e.target.value,10)); setContactPage(0); }} rowsPerPageOptions={[10,50,100]} />
                        </Box>
                    </Modal>
                </Grid>

                {/* WhatsApp messaging policy notice — bottom-right, above snackbars; dismissible via localStorage */}
                {showWhatsAppPolicyNotice && (
                  <Alert
                    severity="info"
                    icon={<Message sx={{ fontSize: '1.1rem' }} />}
                    onClose={() => dismissWhatsAppPolicyNotice(false)}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      bottom: 72,
                      maxWidth: 360,
                      zIndex: 10,
                      py: 0.75,
                      px: 1.5,
                      borderRadius: '8px',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'info.light',
                      '& .MuiAlert-message': {
                        fontSize: '0.78rem',
                        lineHeight: 1.45,
                        color: 'text.primary',
                      },
                      '& .MuiAlert-icon': {
                        alignItems: 'flex-start',
                        pt: 0.25,
                        opacity: 0.9,
                      },
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.8rem', display: 'block', mb: 0.25 }}>
                      Customer-initiated messaging
                    </Typography>
                    For best delivery, wait for the customer to message first. Free-form replies are available within the active conversation window; outside that window, use an approved template.
                    <Box sx={{ mt: 0.75 }}>
                      <Button
                        size="small"
                        onClick={() => dismissWhatsAppPolicyNotice(true)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          fontSize: '0.72rem',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Don&apos;t show again
                      </Button>
                    </Box>
                  </Alert>
                )}
              </Box>
            )}

            {/* ════ SMS ════ */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Box sx={{ bgcolor: 'background.paper', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '5px' }}>
                            <Tabs value={activeMini2Tab} onChange={(_, v) => setActiveMini2Tab(v)} variant="fullWidth">
                                <Tab label="Create" />
                                <Tab label="Outbox" />
                                <Tab label="Templates" />
                                <Tab label="Campaign Contacts" />
                            </Tabs>

                            {activeMini2Tab === 0 && (
                                <form id="contact-form" onSubmit={handleSMSSubmit}>
                                    <Grid container spacing={2} p={2}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth select label="Template" name="template" sx={{ mt: '5px' }} value={selectedTemplateId} onChange={fetchTemplateListContent}>
                                                <MenuItem value="">Select Template</MenuItem>
                                                {templateListData.map((t, i) => (<MenuItem key={i} value={t.id}>{t.name}</MenuItem>))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth select label="Contact Method" name="keyword" sx={{ mt: '5px' }} required value={contactMethod}
                                                onChange={(e) => { setContactMethod(e.target.value); setPhoneNumber(''); }}>
                                                <MenuItem disabled value="">Contact Method</MenuItem>
                                                <MenuItem value="input_number">Input Number</MenuItem>
                                                {contactCampaignData.length > 0 && (<MenuItem value="from_campaign">Campaign Contacts</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {contactMethod === 'input_number' && (
                                                <TextField fullWidth label="Phone Number" name="phone_number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                                            )}
                                            {contactMethod === 'from_campaign' && (
                                                <TextField fullWidth select label="Campaign" name="campaign_name">
                                                    <MenuItem value="" disabled>Select Campaign</MenuItem>
                                                    {campaignContactList.map((c, i) => (<MenuItem key={i} value={c.campaign_id}>{c.campaign_name}</MenuItem>))}
                                                </TextField>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField multiline rows={4} label="SMS" fullWidth name="message" required value={tempContentValue} onChange={(e) => setTempContentValue(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button variant="contained" fullWidth type="submit" disabled={openWait} sx={{ p: '10px', mt: '12px' }}>{openWait ? 'Sending…' : 'Send SMS'}</Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}

                            {activeMini2Tab === 1 && (<>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>#</TableCell><TableCell>Contact</TableCell><TableCell>Message</TableCell><TableCell>Status</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.length > 0 ? data.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell>{r.id}</TableCell><TableCell>{r.recipient}</TableCell><TableCell>{r.message?.slice(0,50)}…</TableCell><TableCell>{r.status}</TableCell><TableCell>{r.date}</TableCell>
                                                    <TableCell>
                                                        <Button onClick={() => { setSelectedOutboxItem(r); setMessageInfoModal(true); }} variant="contained" color="info" sx={{ p:'1px',m:'5px' }}>
                                                            <RemoveRedEye sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )) : (
                                                <TableRow><TableCell colSpan={5} align="center">No records found</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalItems} page={page} onPageChange={(_, v) => setPage(v)} rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }} rowsPerPageOptions={[10,50,100]} />
                                <Modal open={openMessageInfoModal} onClose={() => setMessageInfoModal(false)}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>Message Info</h3>
                                            <Cancel onClick={() => setMessageInfoModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        {selectedOutboxItem && (
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><TextField label="Recipient" fullWidth value={selectedOutboxItem.recipient||''} sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Date" fullWidth value={selectedOutboxItem.date||''} sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={12}><TextField multiline rows={3} label="Message" fullWidth value={selectedOutboxItem.message||''} sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={12}>
                                                    <Button variant="outlined" fullWidth color="success" sx={{ fontWeight:'bold' }} onClick={() => { copyPhoneNumber(selectedOutboxItem.recipient); setMessageInfoModal(false); }}>CALL</Button>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Box>
                                </Modal>
                            </>)}

                            {activeMini2Tab === 2 && (<>
                                <Box sx={{ display:'flex', justifyContent:'flex-end', p:2 }}>
                                    <Button variant="contained" onClick={() => setAddTemplateModal(true)}>Add Template</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor:'background.table' }}>
                                            <TableRow><TableCell>Name</TableCell><TableCell>Content</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell></TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {templateData.length > 0 ? templateData.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell>{r.name}</TableCell><TableCell>{r.content}</TableCell><TableCell>{r.date}</TableCell>
                                                    <TableCell>
                                                        <Button onClick={() => { setSelectedItem(r); setEditTemplateModal(true); }} variant="contained" color="info" sx={{ p:'1px',m:'5px' }}>
                                                            <RemoveRedEye sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )) : (
                                                <TableRow><TableCell colSpan={4} align="center">No records</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalTemplateItems} page={templatepage} onPageChange={(_, v) => setTemplatePage(v)} rowsPerPage={templaterowsPerPage}
                                    onRowsPerPageChange={(e) => { setTemplateRowsPerPage(parseInt(e.target.value,10)); setTemplatePage(0); }} rowsPerPageOptions={[10,50,100]} />
                                <Modal open={openAddTemplateModal} onClose={() => setAddTemplateModal(false)}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>Add Template</h3>
                                            <Cancel onClick={() => setAddTemplateModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <form onSubmit={handleTemplateSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12}><TextField label="Template Name" name="template_name" fullWidth required sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={12}><TextField multiline rows={3} label="Content" name="content" fullWidth sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p:'10px',mt:'10px' }}>Add Template</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                                <Modal open={openEditTemplateModal} onClose={() => setEditTemplateModal(false)}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>Edit Template</h3>
                                            <Cancel onClick={() => setEditTemplateModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <form onSubmit={handleTemplateEditSubmit}>
                                            {selectedItem && (
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12}>
                                                        <TextField label="Name" name="name" fullWidth required value={selectedItem.name||''} onChange={handleInputModChange} sx={{ mt:'5px' }} />
                                                        <TextField name="id" value={selectedItem.id||''} sx={{ display:'none' }} />
                                                    </Grid>
                                                    <Grid item xs={12}><TextField multiline rows={3} label="Content" name="content" fullWidth value={selectedItem.content||''} onChange={handleInputModChange} sx={{ mt:'5px' }} /></Grid>
                                                    <Grid item xs={6}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p:'10px',mt:'10px' }}>Edit</Button></Grid>
                                                    <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p:'10px',mt:'10px' }} onClick={() => handleTemplateDelete(selectedItem.id)}>Delete</Button></Grid>
                                                </Grid>
                                            )}
                                        </form>
                                    </Box>
                                </Modal>
                            </>)}

                            {activeMini2Tab === 3 && (<>
                                <Box sx={{ display:'flex', justifyContent:'flex-end', p:2 }}>
                                    <Button variant="contained" onClick={() => setBulkContactModal(true)}>Upload Campaign Contacts</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor:'background.table' }}>
                                            <TableRow><TableCell>Name</TableCell><TableCell>Added</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell></TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {contactCampaignData.map((r,i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{r.campaign_name}</TableCell><TableCell>{r.added_by}</TableCell><TableCell>{r.date}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="primary" sx={{ p:'1px',m:'5px' }} onClick={() => { fetchSingleContactCampaignData(r.campaign_id); setTableCampaignName(r.campaign_name); setTableCampaignId(r.campaign_id); setContactModal(true); }}>
                                                            <RecentActors sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                        </Button>
                                                        <Button variant="contained" color="info" sx={{ p:'1px',m:'5px' }} onClick={() => { setSelectedItem(r); setContactMessageModal(true); }}>
                                                            <Message sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                        </Button>
                                                        <Button variant="contained" color="error" sx={{ p:'1px',m:'5px' }} onClick={() => { setSelectedItem(r); setContactConfirmModal(true); }}>
                                                            <DeleteForever sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalContactCampaignItems} page={campaignpage} onPageChange={(_, v) => setCampaignPage(v)} rowsPerPage={campaignrowsPerPage}
                                    onRowsPerPageChange={(e) => { setCampaignRowsPerPage(parseInt(e.target.value,10)); setCampaignPage(0); }} rowsPerPageOptions={[10,50,100]} />

                                <Modal open={openBulkContactModal} onClose={() => setBulkContactModal(false)} BackdropProps={{ onClick: (e) => e.stopPropagation() }}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>Add Campaign Contacts</h3>
                                            <Button variant="contained" sx={{ mt:'-10px' }} onClick={downloadTemplate}>Download Template</Button>
                                            <Cancel onClick={() => setBulkContactModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <form onSubmit={handleBulkContactSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12}><TextField label="Campaign Name" fullWidth required value={campaignName} onChange={(e) => setCampaignName(e.target.value)} sx={{ mt:'5px' }} /></Grid>
                                                <Grid item xs={12}><TextField name="file" type="file" fullWidth required onChange={(e) => setSelectedFile(e.target.files[0])} sx={{ mt:'10px' }} /></Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" disabled={!!enableDisableButton} sx={{ p:'10px',mt:'10px' }}>{uploadButtonStatus}</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                                <Modal open={contactConfirmModal} onClose={() => setContactConfirmModal(false)}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>Delete {selectedItem?.campaign_name}</h3>
                                            <Cancel onClick={() => setContactConfirmModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <Box textAlign="center">
                                            <WarningAmber sx={{ fontSize:'70px',color:'red' }} />
                                            <h3>Are you sure? This is irreversible.</h3>
                                        </Box>
                                        <Grid item xs={12} display="flex" justifyContent="space-between">
                                            <Button variant="contained" onClick={() => setContactConfirmModal(false)}>No</Button>
                                            {sessionStorage.getItem('user_level') !== 'agent' && (
                                                <Button variant="contained" sx={{ backgroundColor:'red' }} onClick={() => handleDeleteCampaignContacts(selectedItem.campaign_id)}>Delete</Button>
                                            )}
                                        </Grid>
                                    </Box>
                                </Modal>
                                <Modal open={contactMessageModal} onClose={() => setContactMessageModal(false)} BackdropProps={{ onClick: (e) => e.stopPropagation() }}>
                                    <Box sx={modalStyle}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>{selectedItem?.campaign_name}</h3>
                                            <Cancel onClick={() => setContactMessageModal(false)} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <form onSubmit={handleCampaignMessageUpdate}>
                                            {selectedItem && (
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12}>
                                                        <TextField multiline rows={5} label="Campaign Message" fullWidth required name="message" value={selectedItem.message||''} onChange={handleInputModChange} sx={{ mt:'5px' }} />
                                                    </Grid>
                                                    <TextField name="campaign_id" value={selectedItem.campaign_id||''} sx={{ display:'none' }} />
                                                    <Grid item xs={12}><Button type="submit" variant="contained" color="info" sx={{ p:'10px',mt:'10px' }}>Update</Button></Grid>
                                                </Grid>
                                            )}
                                        </form>
                                    </Box>
                                </Modal>
                                <Modal open={contactModal} onClose={() => { setContactModal(false); setCampaignData([]); setTableCampaignId(''); }} BackdropProps={{ onClick: (e) => e.stopPropagation() }}>
                                    <Box sx={modalStyleLarge}>
                                        <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                                            <h3>{tableCampaignName} – Campaign Contacts</h3>
                                            <Cancel onClick={() => { setContactModal(false); setCampaignData([]); setTableCampaignId(''); }} sx={{ cursor:'pointer' }} />
                                        </Box>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor:'background.table' }}>
                                                    <TableRow><TableCell>Name</TableCell><TableCell>Contact</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell></TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {campaignData.map((r,i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{r.name}</TableCell><TableCell>{r.contact}</TableCell><TableCell>{r.date}</TableCell>
                                                            <TableCell>
                                                                <Button variant="contained" color="error" sx={{ p:'1px',m:'5px' }} onClick={() => deleteCampaignContact(r.id,r.campaign_id)}>
                                                                    <DeleteForever sx={{ fontSize:'1.5rem',m:'5px' }} />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination component="div" count={totalCampaignContactItems} page={campaignContactpage}
                                            onPageChange={(_, v) => { setCampaignContactPage(v); fetchSingleContactCampaignData(tableCampaignId); }}
                                            rowsPerPage={campaignContactrowsPerPage}
                                            onRowsPerPageChange={(e) => { setCampaignContactRowsPerPage(parseInt(e.target.value,5)); setCampaignContactPage(0); }}
                                            rowsPerPageOptions={[5]} />
                                    </Box>
                                </Modal>
                            </>)}
                        </Box>
                    </Grid>
                </Grid>
            )}

            {/* ════ FACEBOOK ════ */}
            {activeTab === 2 && <SocialInbox platform="facebook" />}

            {/* ════ INSTAGRAM ════ */}
            {activeTab === 3 && <SocialInbox platform="instagram" />}

            {/* ════ TWITTER ════ */}
            {activeTab === 4 && <SocialInbox platform="twitter" />}

            {/* ════ WEBCHAT ════ */}
            {activeTab === 5 && (
              <Box>
                <Tabs value={activeWebchatMiniTab} onChange={(_, v) => {
                    setActiveWebchatMiniTab(v);
                    // Avoid showing a live selection on archived tab and vice versa
                    if (v === 1 && selectedWebchatSession?.status === 'active') {
                        setSelectedWebchatSession(null);
                        setWebchatMessages([]);
                        setWebchatStatusText('');
                    }
                    if (v === 0 && selectedWebchatSession?.status === 'ended') {
                        setSelectedWebchatSession(null);
                        setWebchatMessages([]);
                        setWebchatStatusText('');
                    }
                }} variant="fullWidth" sx={{ mb: 1 }}>
                    <Tab label="Live Chats" />
                    <Tab label="Archived" />
                    <Tab label="Offline Leads" />
                </Tabs>

                {/* ── Live Chats ── */}
                {activeWebchatMiniTab === 0 && (
                  <Grid container spacing={0}>
                    {/* Left: sessions */}
                    <Grid item xs={3}>
                        <Box sx={{
                            height: '73vh',
                            bgcolor: 'background.paper',
                            border: '0.1px solid rgba(207,211,212,0.2)',
                            borderRadius: '5px 0 0 5px',
                            p: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2">Active Conversations</Typography>
                                <Tooltip title={webchatSocketConnected ? 'Connected' : 'Disconnected'}>
                                    {webchatSocketConnected
                                        ? <Wifi sx={{ color: 'success.main', fontSize: '1.1rem' }} />
                                        : <WifiOff sx={{ color: 'error.main', fontSize: '1.1rem' }} />}
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search chats"
                                value={webchatSearchTerm}
                                onChange={(e) => {
                                    setWebchatSearchTerm(e.target.value);
                                    debouncedWebchatSearch(e.target.value);
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem' }} /></InputAdornment>,
                                    endAdornment: webchatSearchTerm ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => { setWebchatSearchTerm(''); fetchWebchatSessions(''); }}>
                                                <Close sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Box sx={{ flex: 1, overflowY: 'auto', pr: '2px' }}>
                                {webchatLoading && <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress size={24} /></Box>}
                                {!webchatLoading && webchatSessions.length === 0 && (
                                    <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
                                        No active chats
                                    </Typography>
                                )}
                                {webchatSessions.map((session) => {
                                    const unread = webchatUnreadCounts[session.session_id] || 0;
                                    const name = session.visitor_name || 'Visitor';
                                    const isActive = selectedWebchatSession?.session_id === session.session_id;
                                    return (
                                        <Box
                                            key={session.session_id}
                                            onClick={() => openWebchatConversation(session)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isActive ? 'action.selected' : 'transparent',
                                                p: '8px 6px',
                                                borderRadius: '8px',
                                                mt: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
                                                transition: 'background 0.15s'
                                            }}
                                        >
                                            <Badge badgeContent={unread} color="success" invisible={unread === 0}>
                                                <Avatar sx={{ width: 38, height: 38, bgcolor: avatarColor(name), fontSize: '0.8rem' }}>
                                                    {initials(name)}
                                                </Avatar>
                                            </Badge>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ fontWeight: unread > 0 ? 700 : 500, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {name}
                                                </Box>
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {session.last_message ? session.last_message.slice(0, 30) : 'No messages yet'}
                                                </Box>
                                            </Box>
                                            <Box sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {webchatFormatTime(session.started_at)}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Middle: chat */}
                    <Grid item xs={6}>
                        <Box sx={{
                            bgcolor: 'background.paper',
                            border: '0.1px solid rgba(207,211,212,0.2)',
                            p: '10px',
                            height: '73vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid rgba(207,211,212,0.2)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {selectedWebchatSession && (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: avatarColor(selectedWebchatSession.visitor_name || 'Visitor'), fontSize: '0.75rem' }}>
                                            {initials(selectedWebchatSession.visitor_name || 'Visitor')}
                                        </Avatar>
                                    )}
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                            {selectedWebchatSession ? (selectedWebchatSession.visitor_name || 'Visitor') : 'Select a chat'}
                                        </Typography>
                                        {selectedWebchatSession && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {webchatStatusText || (selectedWebchatSession.status === 'active' ? 'Active' : selectedWebchatSession.status)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {selectedWebchatSession && selectedWebchatSession.status === 'active' && (
                                    <Button variant="outlined" color="error" size="small" onClick={() => handleEndWebchat(selectedWebchatSession.session_id)}>
                                        End Chat
                                    </Button>
                                )}
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto', p: '8px' }}>
                                {webchatMessagesLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <CircularProgress size={32} />
                                    </Box>
                                )}
                                {!webchatMessagesLoading && !selectedWebchatSession && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                                        <Person sx={{ fontSize: '4rem', opacity: 0.3 }} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>Select a chat to view the conversation</Typography>
                                    </Box>
                                )}
                                {!webchatMessagesLoading && selectedWebchatSession && webchatMessages.length === 0 && (
                                    <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                        <Typography variant="body2">No messages yet</Typography>
                                    </Box>
                                )}
                                {!webchatMessagesLoading && webchatMessages.map((msg, idx) => {
                                    const out = msg.origin === 'outbound';
                                    return (
                                        <Box key={idx}>
                                            {webchatShowDateSep(webchatMessages, idx) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', my: 2, gap: 1 }}>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', px: 1, whiteSpace: 'nowrap' }}>
                                                        {webchatFormatDate(msg.date)}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                                </Box>
                                            )}
                                            <Box
                                                ref={idx === webchatMessages.length - 1 ? webchatMessagesEndRef : null}
                                                sx={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', mb: 1, alignItems: 'flex-end', gap: 0.5 }}
                                            >
                                                {!out && (
                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: avatarColor(selectedWebchatSession?.visitor_name || ''), fontSize: '0.6rem', flexShrink: 0 }}>
                                                        {initials(selectedWebchatSession?.visitor_name || '?')}
                                                    </Avatar>
                                                )}
                                                <Box sx={{ maxWidth: '70%' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', textAlign: out ? 'right' : 'left', color: 'text.secondary', mb: '2px', fontSize: '0.65rem', fontWeight: out ? 500 : 400 }}>
                                                        {msg.sender_name || (out ? 'Agent' : 'Visitor')}
                                                    </Typography>
                                                    <Box sx={{
                                                        bgcolor: out ? 'primary.main' : 'background.chatrecipient',
                                                        color: out ? 'primary.contrastText' : 'inherit',
                                                        borderRadius: out ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                                        p: '8px 12px', wordBreak: 'break-word', maxWidth: '100%',
                                                    }}>
                                                        <Typography variant="body2">{msg.message}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: '2px', opacity: 0.75, fontSize: '0.65rem' }}>
                                                            {webchatFormatTime(msg.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {webchatVisitorTyping && selectedWebchatSession?.status === 'active' && (
                                <Box sx={{ px: 1, pb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        Visitor is typing…
                                    </Typography>
                                </Box>
                            )}
                            {selectedWebchatSession && selectedWebchatSession.status === 'active' && (
                                <Paper elevation={2} sx={{ p: '8px 12px', borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                    <form onSubmit={handleSendWebchatMessage} style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                        <TextField
                                            multiline maxRows={4}
                                            placeholder="Type a message..."
                                            fullWidth variant="standard"
                                            value={webchatNewMessage}
                                            onChange={(e) => {
                                                setWebchatNewMessage(e.target.value);
                                                const session = selectedWebchatSessionRef.current;
                                                const sock = webchatSocketRef.current;
                                                if (session?.session_id && sock?.connected && e.target.value.trim()) {
                                                    sock.emit('agent_typing', { session_id: session.session_id });
                                                }
                                            }}
                                            onKeyDown={handleWebchatKeyDown}
                                            disabled={webchatSending}
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ '& .MuiInputBase-root': { py: '4px', fontSize: '0.95rem' } }}
                                        />
                                        <Button type="submit" variant="contained" disabled={!webchatNewMessage.trim() || webchatSending}
                                            sx={{ minWidth: '45px', height: '45px', borderRadius: '50%', p: 0, bgcolor: webchatNewMessage.trim() ? 'primary.main' : 'action.disabledBackground' }}>
                                            {webchatSending ? <CircularProgress size={20} color="inherit" /> : <Send sx={{ fontSize: '20px' }} />}
                                        </Button>
                                    </form>
                                </Paper>
                            )}
                            {selectedWebchatSession && selectedWebchatSession.status !== 'active' && (
                                <Box sx={{ textAlign: 'center', py: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    This chat has ended.
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Right: info panel */}
                    <Grid item xs={3}>
                        <Box sx={{ height: '73vh', bgcolor: 'background.paper', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '0 5px 5px 0', p: '10px', overflowY: 'auto' }}>
                            {selectedWebchatSession ? (<>
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: avatarColor(selectedWebchatSession.visitor_name || 'Visitor'), fontSize: '1.2rem' }}>
                                        {initials(selectedWebchatSession.visitor_name || 'Visitor')}
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedWebchatSession.visitor_name || 'Visitor'}</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Visitor</Typography>
                                {selectedWebchatSession.visitor_email && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Email:</strong> {selectedWebchatSession.visitor_email}</Typography>
                                )}
                                {selectedWebchatSession.visitor_phone && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Phone:</strong> {selectedWebchatSession.visitor_phone}</Typography>
                                )}
                                {selectedWebchatSession.page_url && (
                                    <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-all' }}>
                                        <strong>Page:</strong>{' '}
                                        <a href={selectedWebchatSession.page_url} target="_blank" rel="noreferrer">{selectedWebchatSession.page_url}</a>
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Started:</strong> {selectedWebchatSession.started_at ? `${webchatFormatDate(selectedWebchatSession.started_at)}, ${webchatFormatTime(selectedWebchatSession.started_at)}` : '—'}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Status</Typography>
                                <Typography variant="body2">{selectedWebchatSession.status === 'active' ? 'Active' : 'Ended'}</Typography>
                            </>) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                                    <Person sx={{ fontSize: '3rem', opacity: 0.3 }} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>No chat selected</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                  </Grid>
                )}


                {/* ── Archived (ended) chats ── */}
                {activeWebchatMiniTab === 1 && (
                  <Grid container spacing={0}>
                    {/* Left: archived sessions */}
                    <Grid item xs={3}>
                        <Box sx={{
                            height: '73vh',
                            bgcolor: 'background.paper',
                            border: '0.1px solid rgba(207,211,212,0.2)',
                            borderRadius: '5px 0 0 5px',
                            p: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            <Typography variant="subtitle2">Archived Conversations</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search archived"
                                value={archivedSearchInput}
                                onChange={(e) => {
                                    setArchivedSearchInput(e.target.value);
                                    debouncedArchivedSearch(e.target.value);
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem' }} /></InputAdornment>,
                                    endAdornment: archivedSearchInput ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => {
                                                setArchivedSearchInput('');
                                                setArchivedSearchTerm('');
                                                setArchivedPage(0);
                                            }}>
                                                <Close sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Box sx={{ flex: 1, overflowY: 'auto', pr: '2px' }}>
                                {archivedLoading && <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress size={24} /></Box>}
                                {!archivedLoading && archivedSessions.length === 0 && (
                                    <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
                                        No archived chats
                                    </Typography>
                                )}
                                {archivedSessions.map((session) => {
                                    const name = session.visitor_name || 'Visitor';
                                    const isActive = selectedWebchatSession?.session_id === session.session_id;
                                    return (
                                        <Box
                                            key={session.session_id}
                                            onClick={() => openWebchatConversation(session)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isActive ? 'action.selected' : 'transparent',
                                                p: '8px 6px',
                                                borderRadius: '8px',
                                                mt: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
                                                transition: 'background 0.15s'
                                            }}
                                        >
                                            <Avatar sx={{ width: 38, height: 38, bgcolor: avatarColor(name), fontSize: '0.8rem' }}>
                                                {initials(name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {name}
                                                </Box>
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {session.last_message ? session.last_message.slice(0, 30) : 'No messages'}
                                                </Box>
                                            </Box>
                                            <Box sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {webchatFormatTime(session.ended_at || session.started_at)}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <TablePagination
                                component="div"
                                count={totalArchivedSessions}
                                page={archivedPage}
                                rowsPerPage={archivedRowsPerPage}
                                onPageChange={(_, v) => setArchivedPage(v)}
                                onRowsPerPageChange={(e) => {
                                    setArchivedRowsPerPage(parseInt(e.target.value, 10));
                                    setArchivedPage(0);
                                }}
                                rowsPerPageOptions={[10, 20, 50]}
                                sx={{ borderTop: '1px solid', borderColor: 'divider', '.MuiTablePagination-toolbar': { minHeight: 40, pl: 0 } }}
                            />
                        </Box>
                    </Grid>

                    {/* Middle: read-only transcript */}
                    <Grid item xs={6}>
                        <Box sx={{
                            bgcolor: 'background.paper',
                            border: '0.1px solid rgba(207,211,212,0.2)',
                            p: '10px',
                            height: '73vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid rgba(207,211,212,0.2)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {selectedWebchatSession && selectedWebchatSession.status === 'ended' && (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: avatarColor(selectedWebchatSession.visitor_name || 'Visitor'), fontSize: '0.75rem' }}>
                                            {initials(selectedWebchatSession.visitor_name || 'Visitor')}
                                        </Avatar>
                                    )}
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                            {selectedWebchatSession && selectedWebchatSession.status === 'ended'
                                                ? (selectedWebchatSession.visitor_name || 'Visitor')
                                                : 'Select an archived chat'}
                                        </Typography>
                                        {selectedWebchatSession && selectedWebchatSession.status === 'ended' && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Ended{selectedWebchatSession.ended_at ? ` · ${webchatFormatDate(selectedWebchatSession.ended_at)} ${webchatFormatTime(selectedWebchatSession.ended_at)}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                <Chip label="Read only" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto', p: '8px' }}>
                                {webchatMessagesLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <CircularProgress size={32} />
                                    </Box>
                                )}
                                {!webchatMessagesLoading && !(selectedWebchatSession && selectedWebchatSession.status === 'ended') && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                                        <Person sx={{ fontSize: '4rem', opacity: 0.3 }} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>Select an archived chat to view the transcript</Typography>
                                    </Box>
                                )}
                                {!webchatMessagesLoading && selectedWebchatSession?.status === 'ended' && webchatMessages.length === 0 && (
                                    <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                                        <Typography variant="body2">No messages in this chat</Typography>
                                    </Box>
                                )}
                                {!webchatMessagesLoading && selectedWebchatSession?.status === 'ended' && webchatMessages.map((msg, idx) => {
                                    const out = msg.origin === 'outbound';
                                    return (
                                        <Box key={idx}>
                                            {webchatShowDateSep(webchatMessages, idx) && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', my: 2, gap: 1 }}>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', px: 1, whiteSpace: 'nowrap' }}>
                                                        {webchatFormatDate(msg.date)}
                                                    </Typography>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                                </Box>
                                            )}
                                            <Box
                                                ref={idx === webchatMessages.length - 1 ? webchatMessagesEndRef : null}
                                                sx={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', mb: 1, alignItems: 'flex-end', gap: 0.5 }}
                                            >
                                                {!out && (
                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: avatarColor(selectedWebchatSession?.visitor_name || ''), fontSize: '0.6rem', flexShrink: 0 }}>
                                                        {initials(selectedWebchatSession?.visitor_name || '?')}
                                                    </Avatar>
                                                )}
                                                <Box sx={{ maxWidth: '70%' }}>
                                                    <Typography variant="caption" sx={{ display: 'block', textAlign: out ? 'right' : 'left', color: 'text.secondary', mb: '2px', fontSize: '0.65rem', fontWeight: out ? 500 : 400 }}>
                                                        {msg.sender_name || (out ? 'Agent' : 'Visitor')}
                                                    </Typography>
                                                    <Box sx={{
                                                        bgcolor: out ? 'primary.main' : 'background.chatrecipient',
                                                        color: out ? 'primary.contrastText' : 'inherit',
                                                        borderRadius: out ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                                        p: '8px 12px', wordBreak: 'break-word', maxWidth: '100%',
                                                    }}>
                                                        <Typography variant="body2">{msg.message}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: '2px', opacity: 0.75, fontSize: '0.65rem' }}>
                                                            {webchatFormatTime(msg.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box sx={{ textAlign: 'center', py: 1, color: 'text.secondary', fontSize: '0.85rem', borderTop: '1px solid', borderColor: 'divider' }}>
                                This chat has ended — transcript is read-only.
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right: visitor info */}
                    <Grid item xs={3}>
                        <Box sx={{ height: '73vh', bgcolor: 'background.paper', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '0 5px 5px 0', p: '10px', overflowY: 'auto' }}>
                            {selectedWebchatSession && selectedWebchatSession.status === 'ended' ? (<>
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: avatarColor(selectedWebchatSession.visitor_name || 'Visitor'), fontSize: '1.2rem' }}>
                                        {initials(selectedWebchatSession.visitor_name || 'Visitor')}
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedWebchatSession.visitor_name || 'Visitor'}</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Visitor</Typography>
                                {selectedWebchatSession.visitor_email && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Email:</strong> {selectedWebchatSession.visitor_email}</Typography>
                                )}
                                {selectedWebchatSession.visitor_phone && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Phone:</strong> {selectedWebchatSession.visitor_phone}</Typography>
                                )}
                                {selectedWebchatSession.page_url && (
                                    <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-all' }}>
                                        <strong>Page:</strong>{' '}
                                        <a href={selectedWebchatSession.page_url} target="_blank" rel="noreferrer">{selectedWebchatSession.page_url}</a>
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Started:</strong> {selectedWebchatSession.started_at ? `${webchatFormatDate(selectedWebchatSession.started_at)}, ${webchatFormatTime(selectedWebchatSession.started_at)}` : '—'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Ended:</strong> {selectedWebchatSession.ended_at ? `${webchatFormatDate(selectedWebchatSession.ended_at)}, ${webchatFormatTime(selectedWebchatSession.ended_at)}` : '—'}
                                </Typography>
                                {selectedWebchatSession.agent_name && (
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        <strong>Agent:</strong> {selectedWebchatSession.agent_name}
                                    </Typography>
                                )}
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Status</Typography>
                                <Typography variant="body2">Ended (archived)</Typography>
                            </>) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                                    <Person sx={{ fontSize: '3rem', opacity: 0.3 }} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>No chat selected</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                  </Grid>
                )}

                {/* ── Offline Leads ── */}
                {activeWebchatMiniTab === 2 && (
                  <Box sx={{ bgcolor: 'background.paper', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '5px' }}>
                    <Box sx={{ p: 2 }}>
                        <TextField
                            label="Search leads" variant="outlined" fullWidth size="small"
                            value={offlineLeadsSearch}
                            onChange={(e) => { setOfflineLeadsSearch(e.target.value); setOfflineLeadsPage(0); }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: '1rem' }} /></InputAdornment> }}
                        />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'background.table' }}>
                                <TableRow>
                                    <TableCell>Name</TableCell><TableCell>Phone</TableCell><TableCell>Email</TableCell>
                                    <TableCell>Message</TableCell><TableCell>Page</TableCell><TableCell>Date</TableCell><TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {offlineLeads.length > 0 ? offlineLeads.map((lead) => (
                                    <TableRow key={lead.id}>
                                        <TableCell>{lead.name}</TableCell>
                                        <TableCell>{lead.phone || '—'}</TableCell>
                                        <TableCell>{lead.email || '—'}</TableCell>
                                        <TableCell>{lead.message ? `${lead.message.slice(0, 30)}…` : '—'}</TableCell>
                                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.page_url || '—'}</TableCell>
                                        <TableCell>{lead.date}</TableCell>
                                        <TableCell>
                                            <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => { setSelectedOfflineLead(lead); setOfflineLeadModal(true); }}>
                                                <RemoveRedEye sx={{ fontSize: '1.5rem', m: '5px' }} />
                                            </Button>
                                            {lead.phone && (
                                                <Button variant="contained" color="success" sx={{ p: '1px', m: '5px' }} onClick={() => copyPhoneNumber(lead.phone)}>
                                                    <Phone sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={7} align="center">No offline leads found</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={totalOfflineLeads} page={offlineLeadsPage} rowsPerPage={offlineLeadsRowsPerPage}
                        onPageChange={(_, v) => setOfflineLeadsPage(v)}
                        onRowsPerPageChange={(e) => { setOfflineLeadsRowsPerPage(parseInt(e.target.value, 10)); setOfflineLeadsPage(0); }}
                        rowsPerPageOptions={[10, 50, 100]} />

                    <Modal open={offlineLeadModal} onClose={() => setOfflineLeadModal(false)}>
                        <Box sx={modalStyle}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <h3>Lead: {selectedOfflineLead?.name}</h3>
                                <Cancel onClick={() => setOfflineLeadModal(false)} sx={{ cursor: 'pointer' }} />
                            </Box>
                            {selectedOfflineLead && (
                                <Grid container spacing={1}>
                                    <Grid item xs={6}><TextField label="Name" fullWidth value={selectedOfflineLead.name || ''} InputProps={{ readOnly: true }} sx={{ mt: '5px' }} /></Grid>
                                    <Grid item xs={6}><TextField label="Phone" fullWidth value={selectedOfflineLead.phone || 'N/A'} InputProps={{ readOnly: true }} sx={{ mt: '5px' }} /></Grid>
                                    <Grid item xs={12}><TextField label="Email" fullWidth value={selectedOfflineLead.email || 'N/A'} InputProps={{ readOnly: true }} sx={{ mt: '5px' }} /></Grid>
                                    <Grid item xs={12}><TextField multiline rows={3} label="Message" fullWidth value={selectedOfflineLead.message || 'N/A'} InputProps={{ readOnly: true }} sx={{ mt: '5px' }} /></Grid>
                                    <Grid item xs={12}><TextField label="Page URL" fullWidth value={selectedOfflineLead.page_url || 'N/A'} InputProps={{ readOnly: true }} sx={{ mt: '5px' }} /></Grid>
                                    {selectedOfflineLead.phone && (
                                        <Grid item xs={12}>
                                            <Button variant="outlined" fullWidth color="success" sx={{ fontWeight: 'bold', mt: 1 }} onClick={() => { copyPhoneNumber(selectedOfflineLead.phone); setOfflineLeadModal(false); }}>CALL</Button>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Box>
                    </Modal>
                  </Box>
                )}
              </Box>
            )}

            {/* FIX: in-app media lightbox */}
            <Backdrop open={!!lightboxMedia} onClick={() => setLightboxMedia(null)} sx={{ zIndex: 2000, bgcolor: 'rgba(0,0,0,0.9)' }}>
                {lightboxMedia && (
                    <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                        {lightboxMedia.type === 'video' ? (
                            <video src={lightboxMedia.url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px', display: 'block' }} />
                        ) : (
                            <img src={lightboxMedia.url} alt={lightboxMedia.name} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px', display: 'block' }} />
                        )}
                        <Box sx={{ position: 'absolute', top: -48, right: 0, display: 'flex', gap: 1 }}>
                            <Tooltip title="Download">
                                <IconButton onClick={() => downloadMedia(lightboxMedia.url, lightboxMedia.name)} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}><Download /></IconButton>
                            </Tooltip>
                            <Tooltip title="Close">
                                <IconButton onClick={() => setLightboxMedia(null)} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}><Close /></IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Backdrop>

            {/* Softphone is mounted globally in Sidebar so calls ring on any page */}

            {/* Snackbars */}
            <Snackbar open={openWait} anchorOrigin={{ vertical:'bottom', horizontal:'right' }}>
                <Alert severity="info" onClose={() => setOpenWait(false)}>Sending… please wait.</Alert>
            </Snackbar>
            <Snackbar open={openSuccess} autoHideDuration={4000} onClose={() => setOpenSuccess(false)} anchorOrigin={{ vertical:'bottom', horizontal:'right' }}>
                <Alert severity="success" onClose={() => setOpenSuccess(false)}>{notificationMessage || 'Sent successfully!'}</Alert>
            </Snackbar>
            <Snackbar open={openError} autoHideDuration={6000} onClose={() => setOpenError(false)} anchorOrigin={{ vertical:'bottom', horizontal:'right' }}>
                <Alert severity="error" onClose={() => setOpenError(false)}>{notificationMessage || 'Failed to send. Please try again.'}</Alert>
            </Snackbar>
        </>
    );
};

// ══════════════════════════════════════════════════════════════════════════
// REUSABLE SOCIAL INBOX COMPONENT (Facebook, Instagram, Twitter)
// ══════════════════════════════════════════════════════════════════════════
const SocialInbox = ({ platform }) => {
    const { socket } = useContext(WebSocketContext);
    const [conversations, setConversations] = useState([]);
    const [selectedConv, setSelectedConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const authToken = process.env.AUTH_TOKEN;
    const getHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem(authToken)}`,
        'Content-Type': 'application/json',
    }), [authToken]);

    const fetchConversations = async (term = '') => {
        setLoading(true);
        try {
            const res = await api.get(`/${platform}/getconversations`, { params: { search: term }, headers: getHeaders() });
            setConversations(res.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchConversations(); }, [platform]);

    const openConversation = async (conv) => {
        setSelectedConv(conv);
        setMessages([]);
        setMessagesLoading(true);
        try {
            await api.post(`/${platform}/markread`, { conversation_id: conv.conversation_id }, { headers: getHeaders() });
            const res = await api.get(`/${platform}/getmessages`, { params: { conversation_id: conv.conversation_id }, headers: getHeaders() });
            setMessages(res.data.messages || []);
        } catch(e) { console.error(e); }
        finally { setMessagesLoading(false); }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedConv) return;
        
        const isCommentOrMention = selectedConv.platform === 'comment' || selectedConv.platform === 'mention';
        const endpoint = isCommentOrMention 
            ? (platform === 'twitter' ? `/${platform}/replymention` : `/${platform}/replycomment`)
            : `/${platform}/addmessage`;
        
        let payload;
        if (isCommentOrMention) {
            if (platform === 'twitter') {
                const tweetId = selectedConv.conversation_id.split('_')[1]; // Extract ID from `mention_123`
                payload = { in_reply_to_status_id: tweetId, text_body: newMessage };
            } else {
                payload = { comment_id: selectedConv.parent_id, text_body: newMessage };
            }
        } else {
            payload = { recipient_id: selectedConv.sender_id, text_body: newMessage };
        }

        try {
            await api.post(endpoint, payload, { headers: getHeaders() });
            setNewMessage('');
            fetchConversations(); // refresh sidebar
        } catch(e) { console.error(e); }
    };

    useEffect(() => {
        if (!socket) return;
        const receiveEvent = `${platform.toUpperCase()}_RECEIVE_EVENT`;
        const sendEvent = `${platform.toUpperCase()}_SEND_EVENT_REC`;

        const onReceive = (eventData) => {
            const msg = eventData.data;
            if (msg) {
                if (selectedConv && selectedConv.conversation_id === msg.conversation_id) {
                    setMessages(prev => [...prev, msg]);
                }
                fetchConversations();
            }
        };
        const onSend = (msg) => {
            if (selectedConv && selectedConv.conversation_id === msg.conversation_id) {
                setMessages(prev => [...prev, msg]);
            }
            fetchConversations();
        };

        socket.on(receiveEvent, onReceive);
        socket.on(sendEvent, onSend);

        return () => {
            socket.off(receiveEvent, onReceive);
            socket.off(sendEvent, onSend);
        };
    }, [socket, selectedConv, platform]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const formatTime = (ds) => {
        const d = new Date(ds);
        return (!ds || isNaN(d)) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatMessageDate = (ds) => {
        const d = new Date(ds);
        if (!ds || isNaN(d)) return '';
        if (isToday(d)) return 'Today';
        if (isYesterday(d)) return 'Yesterday';
        if (differenceInDays(new Date(), d) < 7) return format(d, 'EEEE');
        return format(d, 'dd/MM/yyyy');
    };

    const showDateSep = (msgs, idx) => {
        if (idx === 0) return true;
        return new Date(msgs[idx].date).toDateString() !== new Date(msgs[idx - 1].date).toDateString();
    };

    return (
        <Grid container spacing={0}>
            {/* Left: conversations */}
            <Grid item xs={4}>
                <Box sx={{
                    height: '77vh',
                    bgcolor: 'background.paper',
                    border: '0.1px solid rgba(207,211,212,0.2)',
                    borderRadius: '5px 0 0 5px',
                    p: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search conversations"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); fetchConversations(e.target.value); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize:'1rem' }} /></InputAdornment>,
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchTerm(''); fetchConversations(''); }}>
                                        <Close sx={{ fontSize:'1rem' }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Box sx={{ flex:1, overflowY:'auto', pr:'2px' }}>
                        {loading && <Box sx={{ textAlign:'center',mt:2 }}><CircularProgress size={24} /></Box>}
                        {!loading && conversations.length === 0 && (
                            <Typography variant="body2" sx={{ textAlign:'center',mt:3,color:'text.secondary' }}>
                                No conversations yet
                            </Typography>
                        )}
                        {conversations.map((conv) => {
                            const isActive = selectedConv?.conversation_id === conv.conversation_id;
                            const name = conv.sender_name || conv.conversation_id;
                            return (
                                <Box
                                    key={conv.conversation_id}
                                    onClick={() => openConversation(conv)}
                                    sx={{
                                        cursor:'pointer',
                                        bgcolor: isActive ? 'action.selected' : 'transparent',
                                        p:'8px 6px',
                                        borderRadius:'8px',
                                        mt:'4px',
                                        display:'flex',
                                        alignItems:'center',
                                        gap:1,
                                        '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
                                        transition:'background 0.15s'
                                    }}
                                >
                                    <Avatar sx={{ width:38, height:38, bgcolor: avatarColor(name), fontSize:'0.8rem' }}>
                                        {initials(name)}
                                    </Avatar>
                                    <Box sx={{ flex:1, minWidth:0 }}>
                                        <Box sx={{ fontWeight:500, fontSize:'0.875rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                            {name}
                                        </Box>
                                        <Box sx={{ fontSize:'0.75rem', color:'text.secondary', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                            {conv.last_message_text ? conv.last_message_text.slice(0,28) : ''}
                                        </Box>
                                    </Box>
                                    <Box sx={{ fontSize:'0.65rem', color:'text.secondary', whiteSpace:'nowrap', flexShrink:0 }}>
                                        {formatTime(conv.last_message_date)}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Grid>

            {/* Middle: chat */}
            <Grid item xs={8}>
                <Box sx={{
                    bgcolor:'background.paper',
                    border:'0.1px solid rgba(207,211,212,0.2)',
                    borderRadius: '0 5px 5px 0',
                    p:'10px',
                    height:'77vh',
                    display:'flex',
                    flexDirection:'column'
                }}>
                    {/* Header */}
                    <Box sx={{
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                        mb:1,
                        pb:1,
                        borderBottom:'1px solid rgba(207,211,212,0.2)'
                    }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            {selectedConv && (
                                <Avatar sx={{
                                    width:32,
                                    height:32,
                                    bgcolor: avatarColor(selectedConv.sender_name),
                                    fontSize:'0.75rem'
                                }}>
                                    {initials(selectedConv.sender_name || '')}
                                </Avatar>
                            )}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight:600, lineHeight:1.2 }}>
                                    {selectedConv ? (selectedConv.sender_name || selectedConv.conversation_id) : 'Select a conversation'}
                                </Typography>
                                {selectedConv && (
                                    <Typography variant="caption" sx={{ color:'text.secondary', textTransform: 'capitalize' }}>
                                        {selectedConv.platform} • {selectedConv.conversation_id}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Messages */}
                    <Box sx={{ flex:1, overflowY:'auto', p:'8px' }}>
                        {messagesLoading && (
                            <Box sx={{ display:'flex', justifyContent:'center', mt:4 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        {!messagesLoading && !selectedConv && (
                            <Box sx={{
                                display:'flex',
                                flexDirection:'column',
                                alignItems:'center',
                                justifyContent:'center',
                                height:'100%',
                                color:'text.secondary'
                            }}>
                                <Person sx={{ fontSize:'4rem', opacity:0.3 }} />
                                <Typography variant="body2" sx={{ mt:1 }}>Select a conversation to view messages</Typography>
                            </Box>
                        )}
                        {!messagesLoading && selectedConv && messages.length === 0 && (
                            <Box sx={{ textAlign:'center', mt:4, color:'text.secondary' }}>
                                <Typography variant="body2">No messages yet. Send the first reply!</Typography>
                            </Box>
                        )}

                        {!messagesLoading && messages.map((msg, idx) => {
                            const out = msg.origin === 'outbound';
                            const msgType = msg.type || 'text';
                            const isMedia = ['image', 'video', 'audio', 'document', 'photo', 'animated_gif'].includes(msgType);
                            return (
                                <Box key={idx}>
                                    {showDateSep(messages, idx) && (
                                        <Box sx={{ display:'flex', alignItems:'center', my:2, gap:1 }}>
                                            <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                                            <Typography variant="caption" sx={{ color:'text.secondary', px:1, whiteSpace:'nowrap' }}>
                                                {formatMessageDate(msg.date)}
                                            </Typography>
                                            <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                                        </Box>
                                    )}
                                    <Box sx={{ display:'flex', justifyContent: out ? 'flex-end' : 'flex-start', mb:1, alignItems:'flex-end', gap:0.5 }}>
                                        {!out && (
                                            <Avatar sx={{ width:24, height:24, bgcolor: avatarColor(selectedConv?.sender_name), fontSize:'0.6rem', flexShrink:0 }}>
                                                {initials(selectedConv?.sender_name || '?')}
                                            </Avatar>
                                        )}
                                        <Box sx={{ maxWidth:'70%' }}>
                                            <Box sx={{
                                                bgcolor: out ? 'primary.main' : 'background.chatrecipient',
                                                color: out ? 'primary.contrastText' : 'inherit',
                                                borderRadius: out ? '12px 12px 0 12px' : '12px 12px 12px 0',
                                                p:'8px 12px',
                                                wordBreak:'break-word',
                                                maxWidth: '100%',
                                            }}>
                                                {isMedia && msg.media_url && (
                                                    <Box sx={{ mb: 1 }}>
                                                        {msgType === 'image' || msgType === 'photo' ? (
                                                            <img src={msg.media_url} alt="media" style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px', display: 'block' }} />
                                                        ) : msgType === 'video' || msgType === 'animated_gif' ? (
                                                            <video src={msg.media_url} controls autoPlay loop style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px', display: 'block' }} />
                                                        ) : msgType === 'audio' ? (
                                                            <audio src={msg.media_url} controls style={{ width: '100%' }} />
                                                        ) : (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', p: 1, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                                                                <AttachFile sx={{ fontSize: '1.2rem' }} />
                                                                <a href={msg.media_url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>View Media</a>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                {msg.text_body && <Typography variant="body2">{msg.text_body}</Typography>}
                                                <Typography variant="caption" sx={{ display:'block', textAlign:'right', mt:'2px', opacity:0.75, fontSize:'0.65rem' }}>
                                                    {formatTime(msg.date)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input */}
                    {selectedConv && (
                        <Paper elevation={2} sx={{ p: '8px 12px', borderRadius: '12px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'relative' }}>
                            <form onSubmit={handleSend} style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                                <TextField
                                    multiline
                                    maxRows={4}
                                    placeholder="Type a message..."
                                    fullWidth
                                    variant="standard"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ '& .MuiInputBase-root': { py: '4px', fontSize: '0.95rem' } }}
                                />
                                <Button type="submit" variant="contained" disabled={!newMessage.trim()} sx={{ minWidth: '45px', height: '45px', borderRadius: '50%', p: 0, bgcolor: newMessage.trim() ? 'primary.main' : 'action.disabledBackground' }}>
                                    <Send sx={{ fontSize: '20px' }} />
                                </Button>
                            </form>
                        </Paper>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default Integrations;